import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { knowledgeApiError } from '../utils/knowledgeApiError';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash, faRotateRight, faCalendar, faChevronDown, faChevronUp, faChevronRight, faCircleCheck, faCircleInfo, faFileLines, faCircleQuestion, faGlobe, faSliders, faPlus, faSpinner, faMagnifyingGlass, faXmark, faFolder, faPen, faBriefcase, faBoxOpen, faArrowsRotate, faTriangleExclamation, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { useTranslation, Trans } from 'react-i18next';
import { FormControlLabel, Switch, Checkbox, TextField } from "@mui/material";
import { List } from 'react-window';
import ConfirmationModal from './confirmationModal.js';
import KnowledgeModal from './knowledgeModal.js';
import KnowledgeDocumentModal from './knowledgeDocumentModal.js';
import FailedStatusIcon, { FailureReason } from './knowledgeStatus.js';
import FaqModal from './faqModal.js';
import AddWebsiteLinkModal from './addWebsiteLinkModal.js';
import CheckForUpdatesModal from './CheckForUpdatesModal.js';
import UploadFileModal from './uploadFileModal.js';
import AddFaqModal from './addFaqModal.js';
import EditFaqModal from './editFaqModal.js';
import RescrapingSettingsModal from './rescrapingSettingsModal.js';
import ProductsKnowledge from './ProductsKnowledge.js';
import ConnectorSyncSettingsModal from './connectorSyncSettingsModal.js';
import GoogleDrivePickerModal from './GoogleDrivePickerModal.js';
import useUpgrade from '../hooks/useUpgrade.js';
import useBusinessContext from '../hooks/useBusinessContext.js';
import useGenerateBusinessContext, { generateMessage } from '../hooks/useGenerateBusinessContext.js';
import OneDrivePickerModal from './OneDrivePickerModal.js';
import NotionPickerModal from './NotionPickerModal.js';
import NotionLogo from './NotionLogo.js';
import KnowledgeUpgradeModal from './knowledgeUpgradeModal.js';
import InfoTooltip from './InfoTooltip.js';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { guardWrite } from '../utils/permissions';
import { isUrlTrainingHidden } from '../utils/webflowGate';
import '../translations.js';
import './knowledgeOverview.css';
import './businessContext.css';
import './CheckForUpdatesModal.css';

// Grandfathered bot_ids with custom knowledge limits (for customers who exceeded limits before enforcement)
export const KNOWLEDGE_LIMIT_OVERRIDES = {
  '175553387937545633485CvYWO': 2000,  // myhomegroupwestvalley (Smart, had 1182)
  '17692026302245176735seIvJ': 1000,   // ralfausms (Smart, had 940)
  '176458774237426226028dRDkp': 1000,   // happywholehuman (Smart, had 856)
  '176634582475261348833lCglJ': 1000,   // drlisaleit (Smart, had 768)
  '176942995073147847219Mabrb': 1000,   // Nikki Beach Hotels & Resorts (Smart, had 559)
  '176307790486134953418Amgot': 1000,   // heyosmart (Smart, had 442)
  '176324632213623992006MChQt': 1000,   // spare-home (Smart, had 286)
  '176307965253395423299QYzsZ': 500,   // ebtapplication (Smart, had 256)
  '177219070342463355183ZUErd': 5000,  // 97dental (Boost, had 4318)
  '177002942574491059592qCHom': 2000,  // youcandoit (Boost, had 1679)
  '177435540838255211856QPURI': 2000,  // hausermann garaga (free, had 180)
};

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "YYYY-MM-DD" || stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY") {
    return stored;
  }
  return "YYYY-MM-DD";
};

/**
 * Parse a knowledge `created_at` from either the REST payload (ISO / SQL
 * timestamp) or a live WebSocket status message.
 *
 * The WS path sends dd-mm-yyyy, which `new Date()` reads as MM-DD-YYYY: a page
 * refreshed on 07-08-2026 (7 August) became 8 July, so a just-refreshed page
 * looked a month old until the next page load re-read the real timestamp.
 * The dd-mm-yyyy shape is therefore parsed explicitly; ISO and SQL timestamps
 * do not match it and fall through to the native parser unchanged.
 */
const parseKnowledgeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const str = String(value).trim();
  const dmy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return isNaN(d) ? null : d;
  }
  const d = new Date(str);
  return isNaN(d) ? null : d;
};

const formatDate = (str) => {
  if (!str) return "";
  const d = parseKnowledgeDate(str);
  if (!d) return str;

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const pattern = getUserDateFormat();

  if (pattern === "DD-MM-YYYY") {
    return `${dd}-${mm}-${yyyy}`;
  }

  if (pattern === "MM-DD-YYYY") {
    return `${mm}-${dd}-${yyyy}`;
  }

  return `${yyyy}-${mm}-${dd}`;
};

const extractFaqQuestion = (txt) => {
  if (!txt) return "";
  const m = /Question:\s*([\s\S]*?)(?:\n|$)/i.exec(txt);
  return (m?.[1] || txt).trim();
};

const normalizeUrlClient = (u) => {
  if (!u) return "";
  let v = u.trim();
  if (!/^https?:\/\//i.test(v)) v = "https://" + v;
  try {
    const url = new URL(v);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    const path = url.pathname.replace(/\/+$/,"") || "";
    const query = url.search || "";
    return `${url.protocol}//${host}${path}${query}`;
  } catch {
    return v.toLowerCase().replace(/^www\./, "");
  }
};

const groupLinksByDomain = (links) => {
  const grouped = {};
  links.forEach(link => {
    try {
      const url = new URL(link.url_link.startsWith('http') ? link.url_link : 'https://' + link.url_link);
      const domain = url.hostname.replace(/^www\./, '');
      if (!grouped[domain]) grouped[domain] = { rootLinks: [], paths: {} };

      // Get path segments
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length === 0) {
        // Root level link (e.g., example.com/)
        grouped[domain].rootLinks.push(link);
      } else if (pathParts.length === 1) {
        // Single path segment - goes directly under first level folder
        const firstSegment = '/' + pathParts[0];
        if (!grouped[domain].paths[firstSegment]) grouped[domain].paths[firstSegment] = { rootLinks: [], subpaths: {} };
        grouped[domain].paths[firstSegment].rootLinks.push(link);
      } else {
        // Two or more path segments - group by first two levels
        const firstSegment = '/' + pathParts[0];
        const secondSegment = '/' + pathParts[1];
        if (!grouped[domain].paths[firstSegment]) grouped[domain].paths[firstSegment] = { rootLinks: [], subpaths: {} };
        if (!grouped[domain].paths[firstSegment].subpaths[secondSegment]) grouped[domain].paths[firstSegment].subpaths[secondSegment] = [];
        grouped[domain].paths[firstSegment].subpaths[secondSegment].push(link);
      }
    } catch {
      if (!grouped['other']) grouped['other'] = { rootLinks: [], paths: {} };
      grouped['other'].rootLinks.push(link);
    }
  });
  return grouped;
};

const getTotalLinksForPath = (pathData) => {
  if (!pathData) return 0;
  const rootCount = pathData.rootLinks?.length || 0;
  const subpathCount = Object.values(pathData.subpaths || {}).reduce((sum, links) => sum + links.length, 0);
  return rootCount + subpathCount;
};

const getAllLinksForPath = (pathData) => {
  if (!pathData) return [];
  const rootLinks = pathData.rootLinks || [];
  const subpathLinks = Object.values(pathData.subpaths || {}).flat();
  return [...rootLinks, ...subpathLinks];
};

const getTotalLinksForDomain = (domainData) => {
  if (!domainData) return 0;
  const rootCount = domainData.rootLinks?.length || 0;
  const pathCount = Object.values(domainData.paths || {}).reduce((sum, pathData) => sum + getTotalLinksForPath(pathData), 0);
  return rootCount + pathCount;
};

const getAllLinksForDomain = (domainData) => {
  if (!domainData) return [];
  const rootLinks = domainData.rootLinks || [];
  const pathLinks = Object.values(domainData.paths || {}).flatMap(pathData => getAllLinksForPath(pathData));
  return [...rootLinks, ...pathLinks];
};

const KNOWLEDGE_TAB_INFO = {
  links: {
    titleKey: 'website_links',
    text: 'Add website pages your bot should learn from. Refresh changed pages with manual or automatic rescraping, and review the raw text your bot received.',
    textKey: 'knowledge_tab_info_links',
  },
  documents: {
    titleKey: 'documents',
    text: 'Upload files your bot should know. Control whether documents can be shared with visitors during chats, inspect extracted text, or remove outdated files.',
    textKey: 'knowledge_tab_info_documents',
  },
  faq: {
    titleKey: 'qa_label',
    text: 'Create exact question-and-answer pairs for important replies. Add, edit, inspect, or remove Q&As anytime.',
    textKey: 'knowledge_tab_info_faq',
  },
  googledrive: {
    title: 'Google Drive',
    text: 'Import Google Drive files into your bot knowledge. Refresh imported files, view extracted content, or remove files you no longer need.',
    textKey: 'knowledge_tab_info_googledrive',
  },
  onedrive: {
    title: 'OneDrive',
    text: 'Import OneDrive files into your bot knowledge. Refresh imported files, view extracted content, or remove files you no longer need.',
    textKey: 'knowledge_tab_info_onedrive',
  },
  notion: {
    title: 'Notion',
    text: 'Import Notion pages and databases into your bot knowledge. Refresh imported pages, view extracted content, or remove pages you no longer need.',
    textKey: 'knowledge_tab_info_notion',
  },
};

function KnowledgeTabInfo({ activeTab }) {
  const { t } = useTranslation();
  const info = KNOWLEDGE_TAB_INFO[activeTab];
  if (!info) return null;

  return (
    <div className="knowledge-info-box knowledge-tab-info-box">
      <FontAwesomeIcon className="knowledge-info-icon" icon={faCircleInfo} />
      <div className="knowledge-info-text">
        <p>
          <strong>{info.titleKey ? t(info.titleKey) : info.title}</strong> - {t(info.textKey)}
        </p>
      </div>
    </div>
  );
}

function KnowledgeTabBorderDraw() {
  return (
    <svg className="knowledge-tab-border-svg" aria-hidden="true" focusable="false">
      <rect className="knowledge-tab-border-rect" pathLength="100" />
    </svg>
  );
}

const getPathFromUrl = (urlString) => {
  try {
    const url = new URL(urlString.startsWith('http') ? urlString : 'https://' + urlString);
    const path = url.pathname + url.search + url.hash;
    return path === '/' ? '/' : path;
  } catch {
    return urlString;
  }
};

// Helper to check if a path contains any scraping links
const hasScrapingLinksInPath = (pathData) => {
  if (!pathData) return false;

  // Check root links
  if (pathData.rootLinks?.some(link => link.status === 'scraping')) return true;

  // Check subpath links
  const subpaths = pathData.subpaths || {};
  for (const subpathLinks of Object.values(subpaths)) {
    if (subpathLinks.some(link => link.status === 'scraping')) return true;
  }

  return false;
};

// Helper to check if a domain contains any scraping links
const hasScrapingLinksInDomain = (domainData) => {
  if (!domainData) return false;

  // Check root links
  if (domainData.rootLinks?.some(link => link.status === 'scraping')) return true;

  // Check all paths
  const paths = domainData.paths || {};
  for (const pathData of Object.values(paths)) {
    if (hasScrapingLinksInPath(pathData)) return true;
  }

  return false;
};

// Build flat list for virtualization based on expanded state
const ITEMS_PER_PAGE = 200;

const buildFlatTreeData = (groupedLinks, expandedDomains, expandedPaths, visibleCounts) => {
  const flatList = [];
  if (!groupedLinks) return flatList;

  const sortedDomains = Object.entries(groupedLinks).sort(([a], [b]) => a.localeCompare(b));

  // Separate single-link domains (shown as links) from multi-link domains (shown as folders)
  const singleLinkDomains = [];
  const multiLinkDomains = [];

  for (const [domain, domainData] of sortedDomains) {
    const allLinks = getAllLinksForDomain(domainData);
    const totalDomainLinks = getTotalLinksForDomain(domainData);

    if (totalDomainLinks === 1 && allLinks.length === 1) {
      singleLinkDomains.push({ domain, domainData, link: allLinks[0] });
    } else {
      multiLinkDomains.push({ domain, domainData });
    }
  }

  // Sort single-link domains by URL
  singleLinkDomains.sort((a, b) => a.link.url_link.localeCompare(b.link.url_link));

  // Add multi-link domain folders first (expandables at top level)
  for (const { domain, domainData } of multiLinkDomains) {
    const allLinks = getAllLinksForDomain(domainData);
    const linkIds = allLinks.filter(l => l.status !== 'scraping').map(l => l.id);
    const totalDomainLinks = getTotalLinksForDomain(domainData);
    const isDomainExpanded = expandedDomains.has(domain);
    const hasScraping = hasScrapingLinksInDomain(domainData);

    // Add domain row
    flatList.push({
      type: 'domain',
      id: `domain-${domain}`,
      domain,
      domainData,
      linkIds,
      totalCount: totalDomainLinks,
      isExpanded: isDomainExpanded,
      hasScraping,
      depth: 0
    });

    if (isDomainExpanded) {
      // Collect all children (root links + path folders) and paginate them together
      const domainChildrenKey = `domain-children-${domain}`;
      const domainVisibleCount = visibleCounts.get(domainChildrenKey) || ITEMS_PER_PAGE;

      // Build array of all children items at domain level
      // Separate into links and folders, then combine: links first, then folders
      const domainLinks = [];
      const domainFolders = [];

      // Add root links
      const sortedRootLinks = [...(domainData.rootLinks || [])].sort((a, b) => a.url_link.localeCompare(b.url_link));
      for (const link of sortedRootLinks) {
        domainLinks.push({ type: 'rootLink', link });
      }

      // Process path folders - separate single-link paths (as links) from multi-link paths (as folders)
      const sortedPaths = Object.entries(domainData.paths || {}).sort(([a], [b]) => a.localeCompare(b));
      for (const [pathSegment, pathData] of sortedPaths) {
        const allPathLinks = getAllLinksForPath(pathData);
        const totalPathLinks = getTotalLinksForPath(pathData);

        if (totalPathLinks === 1 && allPathLinks.length === 1) {
          // Single-link path - add as link
          domainLinks.push({ type: 'singlePathLink', link: allPathLinks[0] });
        } else {
          // Multi-link path - add as folder
          domainFolders.push({ type: 'pathFolder', pathSegment, pathData });
        }
      }

      // Sort all links together by URL
      domainLinks.sort((a, b) => a.link.url_link.localeCompare(b.link.url_link));

      // Combine: links first, then folders
      const domainChildren = [...domainLinks, ...domainFolders];

      // Apply pagination to domain children
      const domainChildrenToShow = domainChildren.slice(0, domainVisibleCount);
      const hasMoreDomainChildren = domainChildren.length > domainVisibleCount;

      // Render the children
      for (const child of domainChildrenToShow) {
        if (child.type === 'rootLink' || child.type === 'singlePathLink') {
          flatList.push({
            type: 'link',
            id: child.link.id,
            link: child.link,
            depth: 1
          });
        } else if (child.type === 'pathFolder') {
          const pathKey = `${domain}::${child.pathSegment}`;
          const allPathLinks = getAllLinksForPath(child.pathData);
          const pathLinkIds = allPathLinks.filter(l => l.status !== 'scraping').map(l => l.id);
          const totalPathLinks = getTotalLinksForPath(child.pathData);
          const isPathExpanded = expandedPaths.has(pathKey);
          const hasScraping = hasScrapingLinksInPath(child.pathData);

          flatList.push({
            type: 'path',
            id: `path-${domain}-${child.pathSegment}`,
            domain,
            pathSegment: child.pathSegment,
            pathData: child.pathData,
            pathLinkIds,
            totalCount: totalPathLinks,
            isExpanded: isPathExpanded,
            hasScraping,
            depth: 1
          });

          if (isPathExpanded) {
            // Collect path children (root links + subpath folders)
            const pathChildrenKey = `path-children-${domain}-${child.pathSegment}`;
            const pathVisibleCount = visibleCounts.get(pathChildrenKey) || ITEMS_PER_PAGE;

            // Separate into links and folders
            const pathLinks = [];
            const pathFolders = [];

            // Add path root links
            const sortedPathRootLinks = [...(child.pathData.rootLinks || [])].sort((a, b) => a.url_link.localeCompare(b.url_link));
            for (const link of sortedPathRootLinks) {
              pathLinks.push({ type: 'pathRootLink', link });
            }

            // Process subpath folders
            const sortedSubpaths = Object.entries(child.pathData.subpaths || {}).sort(([a], [b]) => a.localeCompare(b));
            for (const [subpathSegment, subpathLinks] of sortedSubpaths) {
              if (subpathLinks.length === 1) {
                // Single-link subpath - add as link
                pathLinks.push({ type: 'singleSubpathLink', link: subpathLinks[0] });
              } else {
                // Multi-link subpath - add as folder
                pathFolders.push({ type: 'subpathFolder', subpathSegment, subpathLinks });
              }
            }

            // Sort all links together by URL
            pathLinks.sort((a, b) => a.link.url_link.localeCompare(b.link.url_link));

            // Combine: links first, then folders
            const pathChildren = [...pathLinks, ...pathFolders];

            const pathChildrenToShow = pathChildren.slice(0, pathVisibleCount);
            const hasMorePathChildren = pathChildren.length > pathVisibleCount;

            for (const pathChild of pathChildrenToShow) {
              if (pathChild.type === 'pathRootLink' || pathChild.type === 'singleSubpathLink') {
                flatList.push({
                  type: 'link',
                  id: pathChild.link.id,
                  link: pathChild.link,
                  depth: 2
                });
              } else if (pathChild.type === 'subpathFolder') {
                const subpathKey = `${domain}::${child.pathSegment}::${pathChild.subpathSegment}`;
                const subpathLinkIds = pathChild.subpathLinks.filter(l => l.status !== 'scraping').map(l => l.id);
                const isSubpathExpanded = expandedPaths.has(subpathKey);
                const hasScraping = pathChild.subpathLinks.some(link => link.status === 'scraping');

                flatList.push({
                  type: 'subpath',
                  id: `subpath-${domain}-${child.pathSegment}-${pathChild.subpathSegment}`,
                  domain,
                  pathSegment: child.pathSegment,
                  subpathSegment: pathChild.subpathSegment,
                  subpathLinks: pathChild.subpathLinks,
                  subpathLinkIds,
                  isExpanded: isSubpathExpanded,
                  hasScraping,
                  depth: 2
                });

                if (isSubpathExpanded) {
                  const subpathChildrenKey = `subpath-children-${domain}-${child.pathSegment}-${pathChild.subpathSegment}`;
                  const subpathVisibleCount = visibleCounts.get(subpathChildrenKey) || ITEMS_PER_PAGE;

                  const sortedSubpathLinks = [...pathChild.subpathLinks].sort((a, b) => a.url_link.localeCompare(b.url_link));
                  const subpathLinksToShow = sortedSubpathLinks.slice(0, subpathVisibleCount);
                  const hasMoreSubpathLinks = sortedSubpathLinks.length > subpathVisibleCount;

                  for (const link of subpathLinksToShow) {
                    flatList.push({
                      type: 'link',
                      id: link.id,
                      link,
                      depth: 3
                    });
                  }

                  if (hasMoreSubpathLinks) {
                    flatList.push({
                      type: 'showMore',
                      id: `showmore-${subpathChildrenKey}`,
                      itemKey: subpathChildrenKey,
                      remainingCount: sortedSubpathLinks.length - subpathVisibleCount,
                      depth: 3
                    });
                  }
                }
              }
            }

            if (hasMorePathChildren) {
              flatList.push({
                type: 'showMore',
                id: `showmore-${pathChildrenKey}`,
                itemKey: pathChildrenKey,
                remainingCount: pathChildren.length - pathVisibleCount,
                depth: 2
              });
            }
          }
        }
      }

      // Add "show more" button for domain children if needed
      if (hasMoreDomainChildren) {
        flatList.push({
          type: 'showMore',
          id: `showmore-${domainChildrenKey}`,
          itemKey: domainChildrenKey,
          remainingCount: domainChildren.length - domainVisibleCount,
          depth: 1
        });
      }
    }
  }

  // Then add single-link domains (as links at top level)
  for (const { link } of singleLinkDomains) {
    flatList.push({
      type: 'link',
      id: link.id,
      link: link,
      depth: 0
    });
  }

  return flatList;
};

// Memoized virtualized row component
const VirtualTreeRow = memo(function VirtualTreeRow({
  index,
  style,
  flatList,
  selectedIds,
  onToggleDomain,
  onTogglePath,
  onToggleLink,
  onExpandDomain,
  onExpandPath,
  onShowMore,
  onView,
  formatDate,
  t
}) {
  const item = flatList[index];

  if (!item) return null;

  const paddingLeft = item.depth * 24;

  if (item.type === 'showMore') {
    return (
      <div style={style} className="virtual-tree-row">
        <div className="knowledge-tree-show-more" style={{ paddingLeft }}>
          <span className="virtual-tree-expand-placeholder"></span>
          <button
            className="knowledge-tree-show-more-btn"
            onClick={() => onShowMore(item.itemKey)}
          >
            {t('show_more')} ({item.remainingCount} {t('more')})
          </button>
        </div>
      </div>
    );
  }

  if (item.type === 'domain') {
    const allSelected = item.linkIds.length > 0 && item.linkIds.every(id => selectedIds.has(id));
    const someSelected = item.linkIds.some(id => selectedIds.has(id)) && !allSelected;

    return (
      <div style={style} className="virtual-tree-row">
        <div className="knowledge-tree-domain-label" style={{ paddingLeft }}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={() => onToggleDomain(item.linkIds, allSelected)}
            size="small"
            sx={{ padding: 0, marginRight: '4px', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
          />
          <span
            className="virtual-tree-expand-btn"
            onClick={() => onExpandDomain(item.domain)}
          >
            <FontAwesomeIcon icon={item.isExpanded ? faChevronDown : faChevronRight} />
          </span>
          <span className="virtual-tree-row-clickable" onClick={() => onExpandDomain(item.domain)}>
            <FontAwesomeIcon icon={faGlobe} style={{ color: '#5616ea', marginRight: '8px' }} />
            <span className="knowledge-tree-domain-name">{item.domain}</span>
            <span className="knowledge-icon-box knowledge-tree-count">
              {item.hasScraping ? (
                <FontAwesomeIcon icon={faSpinner} spin className="knowledge-loading-spinner-icon" />
              ) : (
                item.totalCount
              )}
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (item.type === 'path') {
    const allPathSelected = item.pathLinkIds.length > 0 && item.pathLinkIds.every(id => selectedIds.has(id));
    const somePathSelected = item.pathLinkIds.some(id => selectedIds.has(id)) && !allPathSelected;

    return (
      <div style={style} className="virtual-tree-row">
        <div className="knowledge-tree-path-label" style={{ paddingLeft }}>
          <Checkbox
            checked={allPathSelected}
            indeterminate={somePathSelected}
            onChange={() => onTogglePath(item.pathLinkIds, allPathSelected)}
            size="small"
            sx={{ padding: 0, marginRight: '4px', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
          />
          <span
            className="virtual-tree-expand-btn"
            onClick={() => onExpandPath(item.domain, item.pathSegment)}
          >
            <FontAwesomeIcon icon={item.isExpanded ? faChevronDown : faChevronRight} />
          </span>
          <span className="virtual-tree-row-clickable" onClick={() => onExpandPath(item.domain, item.pathSegment)}>
            <FontAwesomeIcon icon={faFolder} style={{ color: '#5616ea', marginRight: '8px' }} />
            <span className="knowledge-tree-path-name">{item.pathSegment}</span>
            <span className="knowledge-icon-box knowledge-tree-count">
              {item.hasScraping ? (
                <FontAwesomeIcon icon={faSpinner} spin className="knowledge-loading-spinner-icon" />
              ) : (
                item.totalCount
              )}
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (item.type === 'subpath') {
    const allSubpathSelected = item.subpathLinkIds.length > 0 && item.subpathLinkIds.every(id => selectedIds.has(id));
    const someSubpathSelected = item.subpathLinkIds.some(id => selectedIds.has(id)) && !allSubpathSelected;

    return (
      <div style={style} className="virtual-tree-row">
        <div className="knowledge-tree-path-label" style={{ paddingLeft }}>
          <Checkbox
            checked={allSubpathSelected}
            indeterminate={someSubpathSelected}
            onChange={() => onTogglePath(item.subpathLinkIds, allSubpathSelected)}
            size="small"
            sx={{ padding: 0, marginRight: '4px', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
          />
          <span
            className="virtual-tree-expand-btn"
            onClick={() => onExpandPath(item.domain, `${item.pathSegment}::${item.subpathSegment}`)}
          >
            <FontAwesomeIcon icon={item.isExpanded ? faChevronDown : faChevronRight} />
          </span>
          <span className="virtual-tree-row-clickable" onClick={() => onExpandPath(item.domain, `${item.pathSegment}::${item.subpathSegment}`)}>
            <FontAwesomeIcon icon={faFolder} style={{ color: '#5616ea', marginRight: '8px' }} />
            <span className="knowledge-tree-path-name">{item.subpathSegment}</span>
            <span className="knowledge-icon-box knowledge-tree-count">
              {item.hasScraping ? (
                <FontAwesomeIcon icon={faSpinner} spin className="knowledge-loading-spinner-icon" />
              ) : (
                item.subpathLinks.length
              )}
            </span>
          </span>
        </div>
      </div>
    );
  }

  if (item.type === 'link') {
    const link = item.link;
    const checked = selectedIds.has(link.id);
    const disabledRow = link.status === 'scraping';

    return (
      <div style={style} className="virtual-tree-row">
        <div className="knowledge-tree-item-label" style={{ paddingLeft }}>
          <Checkbox
            checked={checked}
            disabled={disabledRow}
            onChange={() => onToggleLink(link.id)}
            size="small"
            sx={{ padding: 0, marginRight: '4px', '&.Mui-checked': { color: '#5616ea' } }}
          />
          <span className="virtual-tree-line-indicator"></span>
          {link.status === 'scraping' ? (
            <FontAwesomeIcon icon={faSpinner} spin className="knowledge-loading-spinner-icon" style={{ marginRight: '8px' }} />
          ) : link.status === 'failed' ? (
            <FailedStatusIcon reason={link.failure_reason} style={{ marginRight: '8px' }} />
          ) : (
            <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" style={{ marginRight: '8px' }} />
          )}
          <span className="knowledge-tree-item-path" title={link.url_link}>
            {item.depth === 0 ? link.url_link : getPathFromUrl(link.url_link)}
          </span>
          <FailureReason status={link.status} reason={link.failure_reason} inline />
          <div className="knowledge-tree-item-actions">
            <span className="knowledge-tree-item-date">
              <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '4px' }} />
              {formatDate(link.created_at)}
            </span>
            <button
              className="knowledge-icon-box knowledge-action-button"
              onClick={() => onView(link.id)}
              disabled={link.status === 'scraping'}
              title={t('view_data')}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
});

function KnowledgeOverview({ selectedBotId, onUsageData, section }) {
  const { t, i18n } = useTranslation();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkDeleteContext, setBulkDeleteContext] = useState({ type: null, ids: [], label: '' });
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
  const [knowledgeDocumentModalOpen, setKnowledgeDocumentModalOpen] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [knowledgeToDelete, setKnowledgeToDelete] = useState(null);
  const [rescrapeModalOpen, setRescrapeModalOpen] = useState(false);
  const [connectorSyncTasks, setConnectorSyncTasks] = useState({});
  const [connectorSyncModalProvider, setConnectorSyncModalProvider] = useState(null);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  // Stable reference: the `?? []` fallback would otherwise hand a fresh array
  // to every memo that depends on it on each render.
  const knowledgeData = useMemo(() => bot?.knowledge_data ?? [], [bot]);
  const websiteLinks = knowledgeData.filter(i => i.type === 'url_link');
  const documents = knowledgeData.filter(i => i.type === 'document');
  const products = knowledgeData.filter(i => i.type === 'product');
  const googleDriveFiles = knowledgeData.filter(i => i.type === 'google_drive');
  const onedriveFiles = knowledgeData.filter(i => i.type === 'onedrive');
  const notionFiles = knowledgeData.filter(i => i.type === 'notion');
  const faqs = knowledgeData.filter(i => i.type === 'faq');
  const googleDriveScrapingCount = googleDriveFiles.filter(f => f.status === 'scraping').length;
  const onedriveScrapingCount = onedriveFiles.filter(f => f.status === 'scraping').length;
  const notionScrapingCount = notionFiles.filter(f => f.status === 'scraping').length;
  // Products and Wix CMS (Site content) are managed under AI Studio → Apps, not the
  // Knowledge Base, and aren't "knowledge sources" the owner adds here — so they
  // don't count toward the source quota / usage bar / add-limit gating.
  const knowledgeCount = knowledgeData.filter(
    i => i.status !== 'failed' && i.type !== 'product' && i.type !== 'cms'
  ).length;
  const knowledgeLimit = KNOWLEDGE_LIMIT_OVERRIDES[selectedBotId] ?? bot?.subscription_plan?.document_limit ?? 10;
  const isAtKnowledgeLimit = knowledgeCount >= knowledgeLimit;
  const remainingSlots = Math.max(0, knowledgeLimit - knowledgeCount);

  // Website-knowledge freshness. created_at is re-stamped on every successful
  // ingest, including the delta-gate skip of an unchanged page, so the newest
  // one answers "when did we last check this site".
  const websiteLastRefreshed = useMemo(() => {
    const stamps = (knowledgeData || [])
      .filter(i => i.type === 'url_link' && i.status === 'completed' && i.created_at)
      .map(i => parseKnowledgeDate(i.created_at)?.getTime())
      .filter(ts => typeof ts === 'number' && !Number.isNaN(ts));
    if (!stamps.length) return null;
    // Clamp so a clock skew between server and browser can never read as tomorrow.
    return Math.min(Math.max(...stamps), Date.now());
  }, [knowledgeData]);

  const daysSinceRefresh = websiteLastRefreshed == null
    ? null
    : Math.floor((Date.now() - websiteLastRefreshed) / 86400000);
  // Stale is stale, regardless of the automatic-rescraping switch. An active
  // schedule that has not refreshed anything in 45 days is not a reason to stay
  // quiet, it is the very case worth flagging: the schedule is not doing its job
  // (plan gate, no base URLs, failing crawls). This also keeps the note in step
  // with the Overview card, which reads the same threshold.
  const isKnowledgeStale = daysSinceRefresh != null && daysSinceRefresh >= 45;
  // Having pages is the only requirement: the backend resolves WHAT to crawl
  // (declared base URLs, else bots.url, else the hosts of the known pages), so a
  // bot trained by pasting links still gets the button.
  const canCheckUpdates = websiteLinks.length > 0;

  // Report usage to the parent section header, but only once real data has
  // loaded — before the bot resolves, the counts default to 0 / 10 and would
  // flash a wrong bar. `loading` flips back to false after every
  // add/delete/rescrape, so the header bar stays in sync without flickering to
  // a stale value during those operations.
  useEffect(() => {
    if (onUsageData && !loading) {
      onUsageData({ knowledgeCount, knowledgeLimit, isAtLimit: isAtKnowledgeLimit });
    }
  }, [knowledgeCount, knowledgeLimit, isAtKnowledgeLimit, onUsageData, loading]);

  const [knowledgeUpgradeModalOpen, setKnowledgeUpgradeModalOpen] = useState(false);
  const [selectedKnowledgeId, setSelectedKnowledgeId] = useState(null);
  const [selectedKnowledgeDocumentId, setSelectedKnowledgeDocumentId] = useState(null);
  const [addWebsiteLinkModalOpen, setAddWebsiteLinkModalOpen] = useState(false);
  const [checkUpdatesModalOpen, setCheckUpdatesModalOpen] = useState(false);
  const [documentIsExpanded, setDocumentIsExpanded] = useState(false);
  // When rendered as a standalone sidebar section (Website Links / FAQs /
  // Documents / …) the type is fixed via `section` and the internal tab
  // bar is hidden. Without it, the full multi-tab Knowledge page renders.
  const [activeTab, setActiveTab] = useState(section || 'links');
  // Which connector provider the active tab maps to (backend provider key).
  const activeConnectorProvider =
    activeTab === 'googledrive' ? 'google_drive'
    : activeTab === 'onedrive' ? 'onedrive'
    : activeTab === 'notion' ? 'notion'
    : null;
  const { requestUnsavedAction, registerUnsavedChangesGuard } = useUnsavedChanges();
  const requestActiveTabChange = useCallback(
    (next) => requestUnsavedAction(() => setActiveTab(next)),
    [requestUnsavedAction]
  );
  useEffect(() => {
    if (section) setActiveTab(section);
  }, [section]);
  // Webflow-managed bots must not see the Website Links source (Webflow
  // Marketplace forbids arbitrary-URL training) — steer the tab away.
  // No-op while the gate is disabled (utils/webflowGate.js).
  useEffect(() => {
    const webflowAccount = isUrlTrainingHidden(
      localStorage.getItem('webflow_connected') === '1',
      bot?.webflow_connected,
      bot?.webflow_hosted
    );
    if (webflowAccount && activeTab === 'links') setActiveTab('documents');
  }, [bot?.webflow_connected, bot?.webflow_hosted, activeTab]);
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [selectedGoogleDriveIds, setSelectedGoogleDriveIds] = useState([]);
  const [selectedOnedriveIds, setSelectedOnedriveIds] = useState([]);
  const [selectedNotionIds, setSelectedNotionIds] = useState([]);
  const websiteSelectAllRef = useRef(null);
  const documentSelectAllRef = useRef(null);
  const googleDriveSelectAllRef = useRef(null);
  const onedriveSelectAllRef = useRef(null);
  const notionSelectAllRef = useRef(null);
  const [googleDriveIsExpanded, setGoogleDriveIsExpanded] = useState(false);
  const [onedriveIsExpanded, setOnedriveIsExpanded] = useState(false);
  const [notionIsExpanded, setNotionIsExpanded] = useState(false);
  const [searchLinks, setSearchLinks] = useState('');
  const [searchDocuments, setSearchDocuments] = useState('');
  const [searchGoogleDrive, setSearchGoogleDrive] = useState('');
  const [searchOnedrive, setSearchOnedrive] = useState('');
  const [searchNotion, setSearchNotion] = useState('');
  const [searchFaq, setSearchFaq] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Deep link from the Overview refresh nudge: /knowledge/website-links?action=check-updates
  // opens the dialog straight away. Waits for the bot so canCheckUpdates is
  // resolved, then drops the param.
  //
  // The param MUST be cleared through the router, not window.history: a raw
  // replaceState leaves react-router's location untouched, so the param survives
  // in React's view and re-opens the dialog on the next mount — which is exactly
  // what switching bots does.
  useEffect(() => {
    if (!bot) return;
    const params = new URLSearchParams(location.search);
    if (params.get('action') !== 'check-updates') return;
    params.delete('action');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
    if (canCheckUpdates) setCheckUpdatesModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot, canCheckUpdates, location.search, location.pathname]);

  // Check for pending tab from navigation (stored by DataConnect before navigate)
  useEffect(() => {
    const pendingTab = localStorage.getItem('pendingKnowledgeTab');
    if (pendingTab && ['links', 'documents', 'products', 'googledrive', 'onedrive', 'notion', 'faq'].includes(pendingTab)) {
      setActiveTab(pendingTab);
      localStorage.removeItem('pendingKnowledgeTab');
    }

    // Check if we should reopen a picker after OAuth flow
    const pendingPicker = localStorage.getItem('pendingOpenPicker');
    if (pendingPicker) {
      localStorage.removeItem('pendingOpenPicker');
      // Small delay to ensure component is ready
      setTimeout(() => {
        if (pendingPicker === 'googledrive') {
          setGoogleDrivePickerOpen(true);
        } else if (pendingPicker === 'onedrive') {
          setOnedrivePickerOpen(true);
        } else if (pendingPicker === 'notion') {
          setNotionPickerOpen(true);
        }
      }, 500);
    }
  }, [location]);

  // If a data-source OAuth attempt was cancelled or denied, the user is
  // returned here with ?<provider>=error — surface a friendly message and
  // clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = ['notion', 'onedrive', 'google_drive'].find(p => params.get(p) === 'error');
    if (provider) {
      params.delete(provider);
      params.delete('reason');
      const qs = params.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (qs ? `?${qs}` : ''));
      const label = provider === 'google_drive' ? 'Google Drive' : provider === 'onedrive' ? 'OneDrive' : 'Notion';
      alert(t('connector_connect_failed', { provider: label }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [addFaqOpen, setAddFaqOpen] = useState(false);
  const [faqSaving, setFaqSaving] = useState(false);
  const [faqSaveError, setFaqSaveError] = useState(null);
  const faqCreateRequest = useRef(null);
  const [editFaqOpen, setEditFaqOpen] = useState(false);
  const [faqToEdit, setFaqToEdit] = useState(null);
  const [googleDrivePickerOpen, setGoogleDrivePickerOpen] = useState(false);
  const [onedrivePickerOpen, setOnedrivePickerOpen] = useState(false);
  const [notionPickerOpen, setNotionPickerOpen] = useState(false);
  const [rescrapingToggleLoading, setRescrapingToggleLoading] = useState(false);
  const [downloadableLoading, setDownloadableLoading] = useState(new Set());

  // Virtualized tree expand state
  const [expandedDomains, setExpandedDomains] = useState(new Set());
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [visibleCounts, setVisibleCounts] = useState(new Map()); // Track visible item count per folder
  const treeContainerRef = useRef(null);

  // Debounced search for performance
  const [debouncedSearchLinks, setDebouncedSearchLinks] = useState('');
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchLinks(searchLinks);
    }, 150);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchLinks]);

  // Memoize filtered website links
  const filteredWebsiteLinks = useMemo(() => {
    if (!debouncedSearchLinks) return websiteLinks;
    const search = debouncedSearchLinks.toLowerCase();
    return websiteLinks.filter(w => w.url_link.toLowerCase().includes(search));
  }, [websiteLinks, debouncedSearchLinks]);

  // Memoize grouped data structure
  const groupedLinks = useMemo(() => groupLinksByDomain(filteredWebsiteLinks), [filteredWebsiteLinks]);

  // Build flat list for virtualization - SUPER FAST
  const flatTreeData = useMemo(() =>
    buildFlatTreeData(groupedLinks, expandedDomains, expandedPaths, visibleCounts),
    [groupedLinks, expandedDomains, expandedPaths, visibleCounts]
  );

  // Use Set for O(1) selection lookups
  const selectedIdsSet = useMemo(() => new Set(selectedWebsiteIds), [selectedWebsiteIds]);

  // Count scraping links
  const scrapingLinksCount = useMemo(() =>
    filteredWebsiteLinks.filter(link => link.status === 'scraping').length,
    [filteredWebsiteLinks]
  );

  // Memoized callbacks for virtualized tree
  const handleExpandDomain = useCallback((domain) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  }, []);

  const handleExpandPath = useCallback((domain, pathSegment) => {
    const pathKey = `${domain}::${pathSegment}`;
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(pathKey)) {
        next.delete(pathKey);
      } else {
        next.add(pathKey);
      }
      return next;
    });
  }, []);

  const handleShowMore = useCallback((itemKey) => {
    setVisibleCounts(prev => {
      const next = new Map(prev);
      const currentCount = next.get(itemKey) || ITEMS_PER_PAGE;
      next.set(itemKey, currentCount + ITEMS_PER_PAGE);
      return next;
    });
  }, []);

  const handleToggleDomain = useCallback((domainLinkIds, allSelected) => {
    if (allSelected) {
      setSelectedWebsiteIds(prev => prev.filter(id => !domainLinkIds.includes(id)));
    } else {
      setSelectedWebsiteIds(prev => [...new Set([...prev, ...domainLinkIds])]);
    }
  }, []);

  const handleTogglePath = useCallback((pathLinkIds, allSelected) => {
    if (allSelected) {
      setSelectedWebsiteIds(prev => prev.filter(id => !pathLinkIds.includes(id)));
    } else {
      setSelectedWebsiteIds(prev => [...new Set([...prev, ...pathLinkIds])]);
    }
  }, []);

  const toggleDocumentExpand = () => setDocumentIsExpanded(!documentIsExpanded);
  const toggleGoogleDriveExpand = () => setGoogleDriveIsExpanded(!googleDriveIsExpanded);
  const toggleOnedriveExpand = () => setOnedriveIsExpanded(!onedriveIsExpanded);
  const toggleNotionExpand = () => setNotionIsExpanded(!notionIsExpanded);

  // ── Business context ──
  // Inline editor with its own load/save state and optimistic version checks.
  const BUSINESS_CONTEXT_MAX = 1000;
  const contextEditor = useBusinessContext(selectedBotId);
  const { draft: businessContext, original: originalBusinessContext,
    saving: businessContextSaving, setDraft: setBusinessContext } = contextEditor;
  // "Regenerate from knowledge" (Robert, 02.11.2026): the new text goes into the field, the owner checks and saves it
  const contextGen = useGenerateBusinessContext(selectedBotId, (i18n.language || 'en').slice(0, 2));
  const contextGenNote = generateMessage(t, contextGen.result);

  const fetchBot = useCallback(async () => {
    if (!selectedBotId) return;
    setLoading(true);
    // The context editor loads independently and stays disabled until its
    // text and version have both arrived.
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${selectedBotId}&include_plan=true`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setBot(data);
      setSelectedWebsiteIds([]);
      setSelectedDocumentIds([]);

    } catch (err) {
      console.error("Error fetching bot:", err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBotId]);

  const refreshBot = useCallback(async () => {
    if (!selectedBotId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${selectedBotId}&include_plan=true`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setBot(data);
      setSelectedWebsiteIds([]);
      setSelectedDocumentIds([]);
    } catch (err) {
      console.error("Error refreshing bot:", err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    }
  }, [selectedBotId]);

  const handleDiscardBusinessContext = () => { contextGen.clear(); return contextEditor.discard(); };

  const handleSaveBusinessContext = async () => {
    if (!selectedBotId) return;
    // Read-only teammates: revert local edits, surface the shared permission
    // modal, and return undefined (NOT false) so the unsaved-changes modal
    // closes and navigation continues.
    if (!guardWrite(() => handleDiscardBusinessContext())) return;
    const ok = await contextEditor.save();
    if (ok) contextGen.clear();
    return ok;
  };

  const businessContextChanged = businessContext !== originalBusinessContext;

  // only over a saved text, so nothing the owner typed is lost
  const handleGenerateBusinessContext = async () => {
    if (businessContextChanged || contextGen.running || !contextEditor.loaded || !guardWrite()) return;
    const r = await contextGen.generate();
    if (r?.status === 'ok' && r.business_context) setBusinessContext(r.business_context);
  };

  // Keep latest handlers in a ref so the guard registration below doesn't
  // re-run on every keystroke.
  const businessContextHandlersRef = useRef({ save: handleSaveBusinessContext, discard: handleDiscardBusinessContext });
  businessContextHandlersRef.current = { save: handleSaveBusinessContext, discard: handleDiscardBusinessContext };

  useEffect(() => {
    return registerUnsavedChangesGuard(`business-context-${selectedBotId}`, {
      when: businessContextChanged,
      onSave: (...args) => businessContextHandlersRef.current.save(...args),
      onDiscard: (...args) => businessContextHandlersRef.current.discard(...args),
    });
  }, [selectedBotId, businessContextChanged, registerUnsavedChangesGuard]);

  useEffect(() => {
    fetchBot();
  }, [fetchBot]);

  useEffect(() => {
    if (!selectedBotId) return;
    let socket;
    let reconnectTimer = null;

    const connectWebSocket = () => {
      socket = new WebSocket(process.env.REACT_APP_API_WEBSOCKET_URL);

      socket.onopen = () => {
        const userId = localStorage.getItem("user_id");
        socket.send(JSON.stringify({ type: "init", user_id: userId, token: localStorage.getItem('token') }));
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.knowledge_id && msg.status) {
            setBot((prev) => {
              if (!prev) return prev;
              const updatedKD = prev.knowledge_data.map((k) =>
                k.id === msg.knowledge_id
                  ? {
                      ...k,
                      status: msg.status,
                      created_at: msg.created_at ?? k.created_at,
                      // The backend sends the customer-facing reason with a
                      // 'failed' event; any other status clears it (mirrors
                      // KnowledgeData.update_status), so the row shows the reason
                      // live instead of only after a page reload.
                      failure_reason: msg.status === 'failed'
                        ? (msg.failure_reason ?? k.failure_reason ?? null)
                        : null,
                    }
                  : k
              );
              return { ...prev, knowledge_data: updatedKD };
            });
            return;
          }

          if (msg.bot_id === selectedBotId && Array.isArray(msg.knowledge_data)) {
            setBot(msg);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };

      socket.onclose = (evt) => {
        if (evt && evt.code === 4401) {
          console.error('knowledgeOverview WS closed 4401 (auth failure); not reconnecting');
          return;
        }
        reconnectTimer = setTimeout(connectWebSocket, 5000);
      };
    };

    connectWebSocket();
    return () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (socket) {
        socket.onclose = null;
        try { socket.close(); } catch {}
      }
    };
  }, [selectedBotId]);

  const toggleWebsiteSelected = (id) => {
    setSelectedWebsiteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDocumentSelected = (id) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleGoogleDriveSelected = (id) => {
    setSelectedGoogleDriveIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleOnedriveSelected = (id) => {
    setSelectedOnedriveIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleNotionSelected = (id) => {
    setSelectedNotionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectableWebsiteIds = websiteLinks.filter(w => w.status !== 'scraping').map(w => w.id);
  const allWebsiteSelected = selectableWebsiteIds.length > 0 && selectableWebsiteIds.every(id => selectedWebsiteIds.includes(id));
  const someWebsiteSelected = selectedWebsiteIds.length > 0 && !allWebsiteSelected;

  useEffect(() => {
    if (websiteSelectAllRef.current) {
      websiteSelectAllRef.current.indeterminate = someWebsiteSelected;
    }
  }, [someWebsiteSelected, selectedWebsiteIds]);

  const toggleSelectAllWebsites = () => {
    if (allWebsiteSelected) {
      setSelectedWebsiteIds([]);
    } else {
      setSelectedWebsiteIds(selectableWebsiteIds);
    }
  };

  const selectableDocumentIds = documents.filter(d => d.status !== 'generating' && d.status !== 'scraping').map(d => d.id);
  const allDocumentSelected = selectableDocumentIds.length > 0 && selectableDocumentIds.every(id => selectedDocumentIds.includes(id));
  const someDocumentSelected = selectedDocumentIds.length > 0 && !allDocumentSelected;

  useEffect(() => {
    if (documentSelectAllRef.current) {
      documentSelectAllRef.current.indeterminate = someDocumentSelected;
    }
  }, [someDocumentSelected, selectedDocumentIds]);

  const toggleSelectAllDocuments = () => {
    if (allDocumentSelected) {
      setSelectedDocumentIds([]);
    } else {
      setSelectedDocumentIds(selectableDocumentIds);
    }
  };

  const selectableGoogleDriveIds = googleDriveFiles.filter(d => d.status !== 'scraping' && d.status !== 'generating').map(d => d.id);
  const allGoogleDriveSelected = selectableGoogleDriveIds.length > 0 && selectableGoogleDriveIds.every(id => selectedGoogleDriveIds.includes(id));
  const someGoogleDriveSelected = selectedGoogleDriveIds.length > 0 && !allGoogleDriveSelected;

  useEffect(() => {
    if (googleDriveSelectAllRef.current) {
      googleDriveSelectAllRef.current.indeterminate = someGoogleDriveSelected;
    }
  }, [someGoogleDriveSelected, selectedGoogleDriveIds]);

  const toggleSelectAllGoogleDrive = () => {
    if (allGoogleDriveSelected) {
      setSelectedGoogleDriveIds([]);
    } else {
      setSelectedGoogleDriveIds(selectableGoogleDriveIds);
    }
  };

  const selectableOnedriveIds = onedriveFiles.filter(d => d.status !== 'scraping' && d.status !== 'generating').map(d => d.id);
  const allOnedriveSelected = selectableOnedriveIds.length > 0 && selectableOnedriveIds.every(id => selectedOnedriveIds.includes(id));
  const someOnedriveSelected = selectedOnedriveIds.length > 0 && !allOnedriveSelected;

  useEffect(() => {
    if (onedriveSelectAllRef.current) {
      onedriveSelectAllRef.current.indeterminate = someOnedriveSelected;
    }
  }, [someOnedriveSelected, selectedOnedriveIds]);

  const toggleSelectAllOnedrive = () => {
    if (allOnedriveSelected) {
      setSelectedOnedriveIds([]);
    } else {
      setSelectedOnedriveIds(selectableOnedriveIds);
    }
  };

  const selectableNotionIds = notionFiles.filter(d => d.status !== 'scraping' && d.status !== 'generating').map(d => d.id);
  const allNotionSelected = selectableNotionIds.length > 0 && selectableNotionIds.every(id => selectedNotionIds.includes(id));
  const someNotionSelected = selectedNotionIds.length > 0 && !allNotionSelected;

  useEffect(() => {
    if (notionSelectAllRef.current) {
      notionSelectAllRef.current.indeterminate = someNotionSelected;
    }
  }, [someNotionSelected, selectedNotionIds]);

  const toggleSelectAllNotion = () => {
    if (allNotionSelected) {
      setSelectedNotionIds([]);
    } else {
      setSelectedNotionIds(selectableNotionIds);
    }
  };

  const isAnyWebsiteSelected = selectedWebsiteIds.length > 0;
  const isAnyDocumentSelected = selectedDocumentIds.length > 0;
  const isAnyGoogleDriveSelected = selectedGoogleDriveIds.length > 0;
  const isAnyOnedriveSelected = selectedOnedriveIds.length > 0;
  const isAnyNotionSelected = selectedNotionIds.length > 0;

  // Check if any selected websites can be rescraped (not currently scraping)
  const canRescrapeSelectedWebsites = selectedWebsiteIds.length > 0 &&
    websiteLinks.some(w => selectedWebsiteIds.includes(w.id) && w.status !== 'scraping');

  const handleOpenDeleteModal = (knowledge) => {
    setKnowledgeToDelete(knowledge);
    setOpenModal(true);
  };

  const handleDeleteKnowledge = async () => {
    if (!knowledgeToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: [knowledgeToDelete.id] }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setOpenModal(false);
      setKnowledgeToDelete(null);
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkDeleteWebsites = async () => {
    if (!isAnyWebsiteSelected) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: selectedWebsiteIds }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setBulkModalOpen(false);
      setBulkDeleteContext({ type: null, ids: [], label: '' });
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkDeleteDocuments = async () => {
    if (!isAnyDocumentSelected) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: selectedDocumentIds }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setBulkModalOpen(false);
      setBulkDeleteContext({ type: null, ids: [], label: '' });
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkDeleteGoogleDrive = async () => {
    if (!isAnyGoogleDriveSelected) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: selectedGoogleDriveIds }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setSelectedGoogleDriveIds([]);
      setBulkModalOpen(false);
      setBulkDeleteContext({ type: null, ids: [], label: '' });
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkDeleteOnedrive = async () => {
    if (!isAnyOnedriveSelected) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: selectedOnedriveIds }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setSelectedOnedriveIds([]);
      setBulkModalOpen(false);
      setBulkDeleteContext({ type: null, ids: [], label: '' });
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkDeleteNotion = async () => {
    if (!isAnyNotionSelected) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}knowledge`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bot_id: selectedBotId, ids: selectedNotionIds }),
        }
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }

      await refreshBot();
    } catch (err) {
      console.error("Error deleting knowledge data:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setSelectedNotionIds([]);
      setBulkModalOpen(false);
      setBulkDeleteContext({ type: null, ids: [], label: '' });
      setSelectedKnowledgeId(null);
      setSelectedKnowledgeDocumentId(null);
    }
  };

  const handleBulkRescrapeNotion = async () => {
    if (!isAnyNotionSelected || !bot) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}rescrape-notion`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bot_id: bot.id,
            user_id: localStorage.getItem("user_id"),
            knowledge_ids: selectedNotionIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to rescrape Notion pages. Status: ${response.status}`);
      }

      // Update local state to show scraping status
      setBot((prev) => {
        if (!prev) return prev;
        const updatedKD = prev.knowledge_data.map((k) =>
          selectedNotionIds.includes(k.id) ? { ...k, status: "scraping" } : k
        );
        return { ...prev, knowledge_data: updatedKD };
      });

      setSelectedNotionIds([]);
    } catch (err) {
      console.error("Error rescraping Notion pages:", err);
      alert(err.message || t('notion_rescrape_failed'));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRescrapeGoogleDrive = async () => {
    if (!isAnyGoogleDriveSelected || !bot) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}rescrape-google-drive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bot_id: bot.id,
            knowledge_ids: selectedGoogleDriveIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to rescrape Google Drive files. Status: ${response.status}`);
      }

      // Update local state to show scraping status
      setBot((prev) => {
        if (!prev) return prev;
        const updatedKD = prev.knowledge_data.map((k) =>
          selectedGoogleDriveIds.includes(k.id) ? { ...k, status: "scraping" } : k
        );
        return { ...prev, knowledge_data: updatedKD };
      });

      setSelectedGoogleDriveIds([]);
    } catch (err) {
      console.error("Error rescraping Google Drive files:", err);
      alert(err.message || t('googledrive_rescrape_failed'));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRescrapeOnedrive = async () => {
    if (!isAnyOnedriveSelected || !bot) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}rescrape-onedrive`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bot_id: bot.id,
            user_id: localStorage.getItem("user_id"),
            knowledge_ids: selectedOnedriveIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to rescrape OneDrive files. Status: ${response.status}`);
      }

      // Update local state to show scraping status
      setBot((prev) => {
        if (!prev) return prev;
        const updatedKD = prev.knowledge_data.map((k) =>
          selectedOnedriveIds.includes(k.id) ? { ...k, status: "scraping" } : k
        );
        return { ...prev, knowledge_data: updatedKD };
      });

      setSelectedOnedriveIds([]);
    } catch (err) {
      console.error("Error rescraping OneDrive files:", err);
      alert(err.message || t('onedrive_rescrape_failed'));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRescrapeWebsites = async () => {
    if (!isAnyWebsiteSelected || !bot) return;

    setLoading(true);
    try {
      // Filter out links with 'scraping' status
      const selectedWebsites = websiteLinks.filter(w => selectedWebsiteIds.includes(w.id) && w.status !== 'scraping');
      const ids = selectedWebsites.map(w => w.id);
      const urls = selectedWebsites.map(w => w.url_link);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}rescrape_link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bot_id: bot.id,
            user_id: localStorage.getItem("user_id"),
            knowledge_ids: ids,
            urls: urls,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to rescrape knowledge data. Status: ${response.status}`);
      }

      setBot((prev) => {
        if (!prev) return prev;
        const updatedKD = prev.knowledge_data.map((k) =>
          ids.includes(k.id) ? { ...k, status: "scraping" } : k
        );
        return { ...prev, knowledge_data: updatedKD };
      });
    } catch (err) {
      console.error("Error rescraping knowledge data:", err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRescrapeLinksFromModal = async ({ urls, bot_id, user_id }) => {
    if (!urls?.length || !bot_id) return;
    setLoading(true);
    try {
      const normSet = new Set(urls.map(normalizeUrlClient));
      const selectedWebsites = (bot?.knowledge_data || []).filter(k => k.type === 'url_link' && normSet.has(normalizeUrlClient(k.url_link)));
      const ids = selectedWebsites.map(w => w.id);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}rescrape_link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot_id,
          user_id,
          knowledge_ids: ids,
          urls: selectedWebsites.map(w => w.url_link)
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to rescrape knowledge data. Status: ${response.status}`);
      }
      setBot((prev) => {
        if (!prev) return prev;
        const updatedKD = prev.knowledge_data.map((k) =>
          ids.includes(k.id) ? { ...k, status: "scraping" } : k
        );
        return { ...prev, knowledge_data: updatedKD };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLinksFromModal = async ({ urls, bot_id, user_id }) => {
    if (!urls?.length || !bot_id) return;
    setLoading(true);
    try {
      const normSet = new Set(urls.map(normalizeUrlClient));
      const selectedWebsites = (bot?.knowledge_data || []).filter(k => k.type === 'url_link' && normSet.has(normalizeUrlClient(k.url_link)));
      const ids = selectedWebsites.map(w => w.id);
      if (!ids.length) {
        await refreshBot();
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}knowledge`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_id: selectedBotId, ids }),
      });
      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(`Failed to delete knowledge data. Status: ${response.status}${msg ? ` - ${msg}` : ""}`);
      }
      await refreshBot();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply one confirmed "Check for updates" run.
   *
   * Order is load-bearing. Rescrape and remove resolve URLs to knowledge ids
   * against the bot snapshot this render closed over, so they must run before
   * the add, whose refreshBot() replaces that snapshot. Both of the later two
   * refresh on their own, so no extra fetch is needed here.
   */
  const handleUpdateCheckApply = async ({ addUrls = [], rescrapeUrls = [], removeUrls = [] }) => {
    if (!bot?.id) return;
    const user_id = localStorage.getItem('user_id');
    if (rescrapeUrls.length) {
      await handleRescrapeLinksFromModal({ urls: rescrapeUrls, bot_id: bot.id, user_id });
    }
    if (removeUrls.length) {
      await handleRemoveLinksFromModal({ urls: removeUrls, bot_id: bot.id, user_id });
    }
    if (addUrls.length) {
      await handleAddWebsiteLink({ urls: addUrls, bot_id: bot.id, user_id });
    }
  };

  const confirmBulkDelete = (type) => {
    if (type === 'links') {
      const count = selectedWebsiteIds.length;
      setBulkDeleteContext({ type: 'links', ids: selectedWebsiteIds, label: `${count} ${t('selected')}` });
      setBulkModalOpen(true);
    } else if (type === 'documents') {
      const count = selectedDocumentIds.length;
      setBulkDeleteContext({ type: 'documents', ids: selectedDocumentIds, label: `${count} ${t('selected')}` });
      setBulkModalOpen(true);
    } else if (type === 'googledrive') {
      const count = selectedGoogleDriveIds.length;
      setBulkDeleteContext({ type: 'googledrive', ids: selectedGoogleDriveIds, label: `${count} ${t('selected')}` });
      setBulkModalOpen(true);
    } else if (type === 'onedrive') {
      const count = selectedOnedriveIds.length;
      setBulkDeleteContext({ type: 'onedrive', ids: selectedOnedriveIds, label: `${count} ${t('selected')}` });
      setBulkModalOpen(true);
    } else if (type === 'notion') {
      const count = selectedNotionIds.length;
      setBulkDeleteContext({ type: 'notion', ids: selectedNotionIds, label: `${count} ${t('selected')}` });
      setBulkModalOpen(true);
    }
  };

  const handleAddWebsiteLink = async (formData) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}scrape_link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        // 403s are handled globally by the permission modal — skip the
        // inline red error message in this case.
        if (response.status === 403) return;
        throw new Error(`Failed to scrape knowledge data. Status: ${response.status}`);
      }

      await refreshBot();
    } catch (err) {
      console.error('Error scraping knowledge data:', err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (fileUrls) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}ingest_document_urls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot_id: bot.id,
          document_urls: fileUrls,
          user_id: localStorage.getItem("user_id")
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to add knowledge document. Status: ${response.status}`);
      }

      await refreshBot();
    } catch (err) {
      console.error('Error adding knowledge document:', err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFaqViaModal = async ({ title, question, answer }) => {
    if (faqSaving) return;
    setFaqSaving(true);
    setFaqSaveError(null);
    try {
      const payload = {
        bot_id: bot.id,
        faq_items: [
          {
            faq_text: `Question:\n${question.trim()}\n\nAnswer:\n${answer.trim()}\n`,
            faq_description: title.trim()
          }
        ],
        user_id: localStorage.getItem("user_id")
      };
      const body = JSON.stringify(payload);
      const requestKey = faqCreateRequest.current?.body === body ? faqCreateRequest.current.key : window.crypto.randomUUID();
      faqCreateRequest.current = { body, key: requestKey };
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}ingest_faq_items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": requestKey },
        body
      });
      if (!response.ok) {
        // 403s are handled globally by the permission modal — close the
        // add-FAQ modal and skip the inline red error message.
        if (response.status === 403) {
          setAddFaqOpen(false);
          return;
        }
        throw await knowledgeApiError(response, t('mcp_settings_save_failed'));
      }
      await refreshBot();
      setActiveTab('faq');
      setAddFaqOpen(false);
      faqCreateRequest.current = null;
    } catch (err) {
      setFaqSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setFaqSaving(false);
    }
  };

  const handleOpenEditFaqModal = async (item) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}knowledge/${item.id}`);
      if (!res.ok) throw new Error(`Failed to fetch FAQ. Status: ${res.status}`);
      const data = await res.json();
      const raw = data.relevant_data || '';
      let q = '', a = '';
      const idx = raw.indexOf('Answer:');
      if (raw.startsWith('Question:') && idx !== -1) {
        q = raw.slice('Question:'.length, idx).trim();
        a = raw.slice(idx + 'Answer:'.length).trim();
      } else {
        a = raw.trim();
      }
      setFaqToEdit({ id: item.id, title: data.description || '', question: q, answer: a, revision: data.revision, pending: null });
      setEditFaqOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleEditFaqViaModal = async ({ knowledgeId, title, question, answer }) => {
    setLoading(true);
    try {
      const payload = {
        bot_id: bot.id,
        knowledge_id: knowledgeId,
        expected_revision: faqToEdit?.revision,
        faq_text: `Question:\n${question.trim()}\n\nAnswer:\n${answer.trim()}\n`,
        faq_description: title.trim(),
        user_id: localStorage.getItem("user_id")
      };
      const body = JSON.stringify(payload);
      const key = faqToEdit?.pending?.body === body ? faqToEdit.pending.key : window.crypto.randomUUID();
      setFaqToEdit(current => current && ({ ...current, pending: { body, key } }));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}edit_faq_item`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Idempotency-Key": key },
        body
      });
      if (!response.ok) throw new Error(t(response.status === 409 ? 'mcp_settings_conflict' : 'mcp_settings_save_failed'));
      await refreshBot();
      setEditFaqOpen(false);
      setFaqToEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenKnowledgeModal = (knowledgeId) => {
    setSelectedKnowledgeId(knowledgeId);
    setKnowledgeModalOpen(true);
  };

  const handleOpenKnowledgeDocumentModal = (knowledgeId) => {
    setSelectedKnowledgeDocumentId(knowledgeId);
    setKnowledgeDocumentModalOpen(true);
  };

  const handleOpenUploadModal = () => setOpenUploadModal(true);

  const handleOpenGoogleDrivePicker = () => setGoogleDrivePickerOpen(true);

  const handleOpenOnedrivePicker = () => setOnedrivePickerOpen(true);

  const handleOpenNotionPicker = () => setNotionPickerOpen(true);

  const handleGoogleDriveFilesSelected = (knowledgeIds, files) => {
    // Optimistically add files to local state immediately so they appear in scraping status
    const newKnowledgeItems = files.map((file, index) => ({
      id: knowledgeIds[index],
      type: 'google_drive',
      document_name: file.name,
      url_link: `gdrive://${file.id}`,
      status: 'scraping',
      created_at: new Date().toISOString(),
    }));

    setBot(prevBot => ({
      ...prevBot,
      knowledge_data: [...(prevBot?.knowledge_data || []), ...newKnowledgeItems],
    }));

    console.log('Google Drive files imported:', knowledgeIds, files);
    // Also refresh from server to ensure data consistency
    refreshBot();
  };

  const handleOnedriveFilesSelected = (knowledgeIds, files) => {
    // Optimistically add files to local state immediately so they appear in scraping status
    const newKnowledgeItems = files.map((file, index) => ({
      id: knowledgeIds[index],
      type: 'onedrive',
      document_name: file.name,
      url_link: `onedrive://${file.id}`,
      status: 'scraping',
      created_at: new Date().toISOString(),
    }));

    setBot(prevBot => ({
      ...prevBot,
      knowledge_data: [...(prevBot?.knowledge_data || []), ...newKnowledgeItems],
    }));

    console.log('OneDrive files imported:', knowledgeIds, files);
    // Also refresh from server to ensure data consistency
    refreshBot();
  };

  const handleNotionFilesSelected = (knowledgeIds, files) => {
    // Optimistically add pages to local state immediately so they appear in scraping status
    const newKnowledgeItems = files.map((file, index) => ({
      id: knowledgeIds[index],
      type: 'notion',
      document_name: file.name,
      url_link: `notion://${file.objectType || 'page'}/${file.id}`,
      status: 'scraping',
      created_at: new Date().toISOString(),
    }));

    setBot(prevBot => ({
      ...prevBot,
      knowledge_data: [...(prevBot?.knowledge_data || []), ...newKnowledgeItems],
    }));

    console.log('Notion pages imported:', knowledgeIds, files);
    // Also refresh from server to ensure data consistency
    refreshBot();
  };

  const handleSaveRescrape = async ({ frequency, knowledgeDataIds, fetchNewLinks, removeOldLinks, baseUrls }) => {
    // Read-only teammates: surface the unified permission modal and
    // bail before hitting the API — prevents the 403 from leaking into
    // the page's error banner.
    if (!guardWrite()) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}rescraping_task/${bot.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          interval: frequency,
          knowledge_data_ids: knowledgeDataIds,
          fetch_new_links: fetchNewLinks,
          remove_old_links: removeOldLinks,
          base_urls: baseUrls ?? []
        })
      });

      if (!response.ok) {
        const err = new Error(`Failed to update rescraping task: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      await refreshBot();
    } catch (err) {
      console.error("Error updating rescraping task:", err);
      // Swallow 403 — the global fetch interceptor already showed the
      // permission-denied modal; surfacing the raw status would replace
      // the page content with an unhelpful "403" line.
      setError(err?.status === 403 ? null : err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRescraping = async (e) => {
    const newState = e.target.checked;
    // Read-only teammates: surface the unified permission modal and
    // bail before flipping any state or hitting the API.
    if (!guardWrite()) return;
    const plan = bot?.subscription_plan?.name;
    const hasAccess = plan === "Boost" || plan === "Ultimo";

    if (!hasAccess) {
      if (newState) {
        setRescrapeModalOpen(true);
      }
      return;
    }

    setRescrapingToggleLoading(true);

    try {
      if (bot.rescraping_task) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}rescraping_task/${bot.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ active: newState }),
        });

        if (!response.ok) {
          const err = new Error(`Failed to update rescraping task: ${response.status}`);
          err.status = response.status;
          throw err;
        }
      } else {
        const allKnowledgeIds = (bot.knowledge_data || []).map(k => k.id);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}rescraping_task`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bot_id: bot.id,
            interval: "monthly",
            active: newState,
            fetch_new_links: false,
            remove_old_links: false,
            knowledge_data_ids: allKnowledgeIds
          }),
        });

        if (!response.ok) {
          const err = new Error(`Failed to create rescraping task: ${response.status}`);
          err.status = response.status;
          throw err;
        }
      }

      await refreshBot();

      // Open modal after backend call completes when activating
      if (newState) {
        setRescrapeModalOpen(true);
      }
    } catch (err) {
      console.error("Error toggling rescraping plan:", err);
      setError(err?.status === 403 ? null : err.message);
    } finally {
      setRescrapingToggleLoading(false);
    }
  };

  // Connector auto-sync (Google Drive / OneDrive / Notion) — configured
  // INDEPENDENTLY PER PROVIDER via the connector_sync_tasks endpoints.
  const loadConnectorSyncTasks = async () => {
    if (!bot?.id) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}connector_sync_tasks/${bot.id}`);
      if (res.ok) {
        const data = await res.json();
        const map = {};
        (data.tasks || []).forEach((task) => { map[task.provider] = task; });
        setConnectorSyncTasks(map);
      }
    } catch (err) {
      console.error("Failed to load connector sync tasks:", err);
    }
  };

  const saveConnectorSyncTask = async (provider, { active, interval, remove_missing, fetch_new }) => {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}connector_sync_task/${bot.id}/${provider}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active, interval, remove_missing, fetch_new }),
    });
    if (!res.ok) {
      const err = new Error(`Failed to update auto-sync: ${res.status}`);
      err.status = res.status;
      throw err;
    }
    await loadConnectorSyncTasks();
  };

  const handleToggleConnectorSync = async (provider, newState) => {
    if (!guardWrite()) return;
    const plan = bot?.subscription_plan?.name;
    const hasAccess = plan === "Boost" || plan === "Ultimo";

    if (!hasAccess) {
      if (newState) setConnectorSyncModalProvider(provider);
      return;
    }

    setRescrapingToggleLoading(true);
    try {
      const current = connectorSyncTasks[provider] || {};
      await saveConnectorSyncTask(provider, {
        active: newState,
        interval: current.interval || "monthly",
        remove_missing: current.remove_missing || false,
        fetch_new: current.fetch_new || false,
      });
      if (newState) setConnectorSyncModalProvider(provider);
    } catch (err) {
      console.error("Error toggling connector auto-sync:", err);
      setError(err?.status === 403 ? null : err.message);
    } finally {
      setRescrapingToggleLoading(false);
    }
  };

  const handleSaveConnectorSync = async ({ frequency, fetchNew, removeOldLinks }) => {
    const provider = connectorSyncModalProvider;
    if (!provider || !guardWrite()) return;
    setLoading(true);
    try {
      await saveConnectorSyncTask(provider, {
        active: true,
        interval: frequency,
        remove_missing: removeOldLinks,
        // Google Drive can't enumerate folder contents, so never request it.
        fetch_new: provider === 'google_drive' ? false : fetchNew,
      });
    } catch (err) {
      console.error("Error updating connector auto-sync settings:", err);
      setError(err?.status === 403 ? null : err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnectorSyncTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot?.id]);

  const handleToggleDownloadable = async (ids, newValue) => {
    if (!guardWrite()) return;
    setDownloadableLoading(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}knowledge/downloadable`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bot_id: selectedBotId, ids, downloadable: newValue }),
      });
      if (!response.ok) throw new Error("Failed to update downloadable status");

      setBot(prevBot => ({
        ...prevBot,
        knowledge_data: (prevBot?.knowledge_data || []).map(k =>
          ids.includes(k.id) ? { ...k, downloadable: newValue } : k
        ),
      }));
    } catch (err) {
      console.error("Error toggling downloadable:", err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setDownloadableLoading(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const onUpgradeComplete = async () => {
    setLoading(true);
    await refreshBot();
    setLoading(false);
  };

  const { handleUpgradeClick: upgradeClickRaw, UpgradeModal, isUpgradeLoading } = useUpgrade({ onComplete: onUpgradeComplete });

  const handleUpgradeClick = async (bot) => {
    setRescrapeModalOpen(false);
    await upgradeClickRaw(bot);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {t('error')}: {error}
      </div>
    );
  }

  if (!bot) {
    return <p>{t('no_bot_selected')}</p>;
  }

  // Three independent reasons to hide every URL-training surface:
  //  1. the account holds a Webflow OAuth token (localStorage, set at login),
  //  2. the bot's owner holds one (per-bot flag),
  //  3. the owner declared THIS agent's site as Webflow-hosted when creating it.
  // Always false while the gate is disabled (utils/webflowGate.js).
  const isWebflowManaged = isUrlTrainingHidden(
    localStorage.getItem('webflow_connected') === '1',
    !!bot.webflow_connected,
    !!bot.webflow_hosted
  );

  return (
    <div className="knowledge-parent-container">
      {!section && (
      <div className="knowledge-tabs">
        {!isWebflowManaged && (
        <button
          className={activeTab === 'links' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('links')}
        >
          <FontAwesomeIcon icon={faGlobe} style={{ color: '#5616ea' }} /> {t('website_links')}
          <span className="knowledge-tab-count">{websiteLinks.length}</span>
          <KnowledgeTabBorderDraw />
        </button>
        )}

        <button
          className={activeTab === 'documents' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('documents')}
        >
          <FontAwesomeIcon icon={faFileLines} style={{ color: '#5616ea' }} /> {t('documents')}
          <span className="knowledge-tab-count">{documents.length}</span>
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'products' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('products')}
        >
          <FontAwesomeIcon icon={faBoxOpen} style={{ color: '#5616ea' }} /> {t('products')}
          <span className="knowledge-tab-count">{products.length}</span>
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'faq' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('faq')}
        >
          <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#5616ea' }} /> {t('qa_label')}
          <span className="knowledge-tab-count">{faqs.length}</span>
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'business_context' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('business_context')}
        >
          <FontAwesomeIcon icon={faBriefcase} style={{ color: '#5616ea' }} /> {t('business_context')}
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'googledrive' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('googledrive')}
        >
          <svg width="20" height="18" viewBox="0 0 87.3 78" style={{ marginRight: '2px' }}>
            <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/>
            <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"/>
            <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"/>
            <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/>
            <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/>
            <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/>
          </svg>
          Google Drive
          {/* <span className="knowledge-tab-badge">New</span> */}
          <span className="knowledge-tab-count">{googleDriveFiles.length}</span>
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'onedrive' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('onedrive')}
        >
          <img src="/onedrive.png" alt="OneDrive" style={{ height: '18px', width: 'auto', marginRight: '4px' }} />
          OneDrive
          {/* <span className="knowledge-tab-badge">New</span> */}
          <span className="knowledge-tab-count">{onedriveFiles.length}</span>
          <KnowledgeTabBorderDraw />
        </button>

        <button
          className={activeTab === 'notion' ? 'knowledge-tab active' : 'knowledge-tab'}
          onClick={() => requestActiveTabChange('notion')}
        >
          <NotionLogo size={18} style={{ marginRight: '4px' }} />
          Notion
          <span className="knowledge-tab-count">{notionFiles.length}</span>
          <KnowledgeTabBorderDraw />
        </button>
      </div>
      )}

      {!section && <KnowledgeTabInfo activeTab={activeTab} />}

      {activeTab === 'links' && !isWebflowManaged && (
        <>
          <div className="knowledge-links-actions">
            <button
              className="knowledge-add-button"
              title={t('add_link')}
              onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : setAddWebsiteLinkModalOpen(true)}
            >
              <div className="knowledge-add-button-text"><FontAwesomeIcon icon={faPlus} /> {t('add_link')}</div>
            </button>

            {/* Only meaningful once the agent has learned pages that can be
                re-checked against their source. */}
            {canCheckUpdates && (
              <>
                <button
                  className="knowledge-check-updates-button"
                  title={t('check_for_updates_hint')}
                  onClick={() => setCheckUpdatesModalOpen(true)}
                >
                  <FontAwesomeIcon icon={faArrowsRotate} /> {t('check_for_updates')}
                </button>

                <span className={`knowledge-freshness${isKnowledgeStale ? ' is-stale' : ''}`}>
                  {/* A warning triangle only when it IS a warning; otherwise a
                      neutral info mark, so the row does not cry wolf. */}
                  <FontAwesomeIcon
                    icon={isKnowledgeStale ? faTriangleExclamation : faCircleInfo}
                    className="knowledge-freshness-icon"
                  />
                  <span>
                    {daysSinceRefresh == null ? (
                      <Trans i18nKey="knowledge_never_refreshed" components={{ strong: <strong /> }} />
                    ) : daysSinceRefresh === 0 ? (
                      <Trans i18nKey="knowledge_refreshed_today" components={{ strong: <strong /> }} />
                    ) : (
                      <Trans
                        i18nKey="knowledge_refreshed_days_ago"
                        count={daysSinceRefresh}
                        components={{ strong: <strong /> }}
                      />
                    )}
                  </span>
                </span>
              </>
            )}
          </div>

          <div className='rescraping-activate-settings-container'>
            {rescrapingToggleLoading ? (
              <div className='rescraping-toggle-loading'>
                <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
                <span>{t("activate-auto-rescraping")}</span>
              </div>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!(bot.rescraping_task && bot.rescraping_task.active)}
                    onChange={handleToggleRescraping}
                    disabled={false}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5616ea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#5616ea',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#ccc',
                      },
                    }}
                  />
                }
                className='rescraping-toggle'
                label={t("activate-auto-rescraping")}
              />
            )}            {bot.rescraping_task?.active && (
              <button
                className='rescraping-settings-button'
                onClick={() => setRescrapeModalOpen(true)}
              >
                <FontAwesomeIcon icon={faSliders} />
              </button>
            )}
          </div>
          <div className="knowledge-add-button-parent">
            <div className="knowledge-container">
              <div className="knowledge-header">
                <div className="header-title-with-search">
                  <h3 className="header-title">{t('website_links')}</h3>
                  <div className="knowledge-search-container">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                    <input
                      type="text"
                      className="knowledge-search-input"
                      placeholder={t('search') || 'Search...'}
                      value={searchLinks}
                      onChange={(e) => setSearchLinks(e.target.value)}
                    />
                    {searchLinks && (
                      <button
                        type="button"
                        className="knowledge-search-clear"
                        onClick={() => setSearchLinks('')}
                        aria-label={t('clear_search')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>
                    {selectedWebsiteIds.length} {t('selected')}
                  </span>

                  <button
                    className="knowledge-action-button"
                    onClick={handleBulkRescrapeWebsites}
                    disabled={!canRescrapeSelectedWebsites}
                    title={canRescrapeSelectedWebsites ? t('rescrape') : t('cannot_rescrape_scraping')}
                    style={{ opacity: canRescrapeSelectedWebsites ? 1 : 0.5, cursor: canRescrapeSelectedWebsites ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </button>
                  <button
                    className="knowledge-action-button"
                    onClick={() => confirmBulkDelete('links')}
                    disabled={!isAnyWebsiteSelected}
                    title={t('remove')}
                    style={{ opacity: isAnyWebsiteSelected ? 1 : 0.5, cursor: isAnyWebsiteSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              {websiteLinks.length > 0 && (
                <div className="knowledge-content knowledge-tree-content" ref={treeContainerRef} style={{gap: 0}}>
                  <div className="knowledge-tree-select-all-row">
                    <Checkbox
                      inputRef={websiteSelectAllRef}
                      checked={allWebsiteSelected}
                      indeterminate={someWebsiteSelected}
                      onChange={toggleSelectAllWebsites}
                      title={allWebsiteSelected ? t('unselect') : t('select')}
                      size="small"
                      sx={{ padding: 0, marginRight: '8px', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
                    />
                    <span className="knowledge-tree-select-all-label">{t('select_all')}</span>
                    {scrapingLinksCount > 0 && (
                      <div className="scraping-links-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin className="scraping-indicator-icon" />
                        <span className="scraping-indicator-text">
                          {t('links_currently_scraping', { count: scrapingLinksCount })}
                        </span>
                      </div>
                    )}
                    <span className="knowledge-icon-box knowledge-tree-count">{filteredWebsiteLinks.length}</span>
                  </div>
                  <List
                    height={Math.min(flatTreeData.length * 36, 500)}
                    rowCount={flatTreeData.length}
                    rowHeight={36}
                    width="100%"
                    rowComponent={VirtualTreeRow}
                    rowProps={{
                      flatList: flatTreeData,
                      selectedIds: selectedIdsSet,
                      onToggleDomain: handleToggleDomain,
                      onTogglePath: handleTogglePath,
                      onToggleLink: toggleWebsiteSelected,
                      onExpandDomain: handleExpandDomain,
                      onExpandPath: handleExpandPath,
                      onShowMore: handleShowMore,
                      onView: handleOpenKnowledgeModal,
                      formatDate,
                      t
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'documents' && (
        <>
          <button
            className="knowledge-add-button"
            title={t('add_document')}
            onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : handleOpenUploadModal()}
          >
            <div className="knowledge-add-button-text"><FontAwesomeIcon icon={faPlus} /> {t('add_document')}</div>
          </button>
          <div className="knowledge-add-button-parent">
            <div className={`knowledge-container ${documentIsExpanded ? '' : 'collapsed'}`}>
              <div className="knowledge-header">
                <div className="header-title-with-search">
                  <h3 className="header-title">{t('documents')}</h3>
                  <div className="knowledge-search-container">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                    <input
                      type="text"
                      className="knowledge-search-input"
                      placeholder={t('search') || 'Search...'}
                      value={searchDocuments}
                      onChange={(e) => setSearchDocuments(e.target.value)}
                    />
                    {searchDocuments && (
                      <button
                        type="button"
                        className="knowledge-search-clear"
                        onClick={() => setSearchDocuments('')}
                        aria-label={t('clear_search')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>
                    {selectedDocumentIds.length} {t('selected')}
                  </span>

                  <button
                    className="knowledge-action-button"
                    onClick={() => confirmBulkDelete('documents')}
                    disabled={!isAnyDocumentSelected}
                    title={t('remove')}
                    style={{ opacity: isAnyDocumentSelected ? 1 : 0.5, cursor: isAnyDocumentSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>

                  <p style={{ marginLeft: '8px' }}>{t('count')}: {documents.length}</p>
                </div>
              </div>

              {documents.length > 0 && (
                <div className="knowledge-content">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', fontSize: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Checkbox
                        inputRef={documentSelectAllRef}
                        checked={allDocumentSelected}
                        indeterminate={someDocumentSelected}
                        onChange={toggleSelectAllDocuments}
                        title={allDocumentSelected ? t('unselect') : t('select')}
                        size="small"
                        sx={{ padding: 0, marginRight: '4px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
                      />
                      <span>{t('select_all')}</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <div><InfoTooltip text={t('enable_downloads_all_tooltip')} /></div>
                      <span>{t('enable_downloads_all')}</span>
                      <Switch
                        checked={documents.length > 0 && documents.every(d => d.downloadable)}
                        onChange={(e) => {
                          const allIds = documents.map(d => d.id);
                          handleToggleDownloadable(allIds, e.target.checked);
                        }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#5616ea',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#5616ea',
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: '#ccc',
                          },
                        }}
                      />
                    </label>
                  </div>
                  {(documentIsExpanded
                    ? [...documents].filter(d => d.document_name.toLowerCase().includes(searchDocuments.toLowerCase())).sort((a, b) => a.document_name.length - b.document_name.length)
                    : [...documents].filter(d => d.document_name.toLowerCase().includes(searchDocuments.toLowerCase())).sort((a, b) => a.document_name.length - b.document_name.length).slice(0, 3)
                  ).map((doc, index) => {
                    const checked = selectedDocumentIds.includes(doc.id);
                    const disabledRow = doc.status === 'generating' || doc.status === 'scraping';
                    return (
                      <React.Fragment key={doc.id}>
                        <div className="knowledge-item">
                          <h3 className="knowledge-link" title={doc.document_name}>
                            <div className="knowledge-status-link" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <Checkbox
                                checked={checked}
                                disabled={disabledRow}
                                onChange={() => toggleDocumentSelected(doc.id)}
                                title={checked ? t('unselect') : t('select')}
                                size="small"
                                sx={{ padding: 0, marginRight: '4px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' } }}
                              />
                              {(doc.status === 'generating' || doc.status === 'scraping') ? (
                                <div className="knowledge-loading-spinner" />
                              ) : doc.status === 'failed' ? (
                                <FailedStatusIcon reason={doc.failure_reason} />
                              ) : (
                                <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />
                              )}
                              {doc.document_name}
                            </div>
                          </h3>
                          <FailureReason status={doc.status} reason={doc.failure_reason} />

                          <div className="knowledge-date-action-container">
                            <div className="knowledge-created-at">
                              <div className="knowledge-created-at-icon">
                                <FontAwesomeIcon icon={faCalendar} />
                              </div>
                              <p>{formatDate(doc.created_at)}</p>
                            </div>

                            <div className="knowledge-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <button
                                className="knowledge-action-button"
                                onClick={() => handleOpenKnowledgeDocumentModal(doc.id)}
                                disabled={doc.status === 'generating'}
                                title={t('view_data')}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              {downloadableLoading.has(doc.id) ? (
                                <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', fontSize: '14px', marginLeft: '8px', marginRight: '8px' }} />
                              ) : (
                                <Switch
                                  checked={doc.downloadable || false}
                                  onChange={(e) => handleToggleDownloadable([doc.id], e.target.checked)}
                                  disabled={disabledRow}
                                  size="small"
                                  title={t('enable_download')}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#5616ea',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#5616ea',
                                    },
                                    '& .MuiSwitch-track': {
                                      backgroundColor: '#ccc',
                                    },
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>

                        {index < documents.length - 1 && <div className="knowledge-item-separator" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {documents.length > 3 && (
                <button className="expand-toggle" onClick={toggleDocumentExpand}>
                  <FontAwesomeIcon icon={documentIsExpanded ? faChevronUp : faChevronDown} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'products' && (
        <ProductsKnowledge
          bot={bot}
          hideWebsiteSource={isWebflowManaged}
          products={products}
          onRefresh={refreshBot}
          isAtKnowledgeLimit={isAtKnowledgeLimit}
          onUpgrade={() => setKnowledgeUpgradeModalOpen(true)}
          remainingSlots={remainingSlots}
        />
      )}

      {activeTab === 'googledrive' && (
        <>
          <button
            className="knowledge-add-button"
            title={t('import_from_google_drive')}
            onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : handleOpenGoogleDrivePicker()}
          >
            <div className="knowledge-add-button-text">
              <FontAwesomeIcon icon={faPlus} /> {t('import_from_google_drive')}
            </div>
          </button>
          <div className='rescraping-activate-settings-container'>
            {rescrapingToggleLoading ? (
              <div className='rescraping-toggle-loading'>
                <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
                <span>{t("auto_sync_files")}</span>
              </div>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!connectorSyncTasks[activeConnectorProvider]?.active}
                    onChange={(e) => handleToggleConnectorSync(activeConnectorProvider, e.target.checked)}
                    disabled={false}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5616ea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#5616ea',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#ccc',
                      },
                    }}
                  />
                }
                className='rescraping-toggle'
                label={t("auto_sync_files")}
              />
            )}            {connectorSyncTasks[activeConnectorProvider]?.active && (
              <button
                className='rescraping-settings-button'
                onClick={() => setConnectorSyncModalProvider(activeConnectorProvider)}
              >
                <FontAwesomeIcon icon={faSliders} />
              </button>
            )}
          </div>
          <div className="knowledge-add-button-parent">
            <div className={`knowledge-container ${googleDriveIsExpanded ? '' : 'collapsed'}`}>
              <div className="knowledge-header">
                <div className="header-title-with-search">
                  <h3 className="header-title">Google Drive</h3>
                  <div className="knowledge-search-container">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                    <input
                      type="text"
                      className="knowledge-search-input"
                      placeholder={t('search') || 'Search...'}
                      value={searchGoogleDrive}
                      onChange={(e) => setSearchGoogleDrive(e.target.value)}
                    />
                    {searchGoogleDrive && (
                      <button
                        type="button"
                        className="knowledge-search-clear"
                        onClick={() => setSearchGoogleDrive('')}
                        aria-label={t('clear_search')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>
                    {selectedGoogleDriveIds.length} {t('selected')}
                  </span>

                  <button
                    className="knowledge-action-button"
                    onClick={handleBulkRescrapeGoogleDrive}
                    disabled={!isAnyGoogleDriveSelected}
                    title={t('rescrape')}
                    style={{ opacity: isAnyGoogleDriveSelected ? 1 : 0.5, cursor: isAnyGoogleDriveSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </button>
                  <button
                    className="knowledge-action-button"
                    onClick={() => confirmBulkDelete('googledrive')}
                    disabled={!isAnyGoogleDriveSelected}
                    title={t('remove')}
                    style={{ opacity: isAnyGoogleDriveSelected ? 1 : 0.5, cursor: isAnyGoogleDriveSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              {googleDriveFiles.length > 0 && (
                <div className="knowledge-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '10px', fontSize: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}>
                      <input
                        ref={googleDriveSelectAllRef}
                        type="checkbox"
                        checked={allGoogleDriveSelected}
                        onChange={toggleSelectAllGoogleDrive}
                        title={allGoogleDriveSelected ? t('unselect') : t('select')}
                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                      />
                      <span>{t('select_all')}</span>
                    </label>
                    {googleDriveScrapingCount > 0 && (
                      <div className="scraping-links-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin className="scraping-indicator-icon" />
                        <span className="scraping-indicator-text">
                          {t('files_currently_importing', { count: googleDriveScrapingCount })}
                        </span>
                      </div>
                    )}
                  </div>
                  {(googleDriveIsExpanded
                    ? [...googleDriveFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchGoogleDrive.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length)
                    : [...googleDriveFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchGoogleDrive.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length).slice(0, 3)
                  ).map((file, index) => {
                    const checked = selectedGoogleDriveIds.includes(file.id);
                    const disabledRow = file.status === 'scraping' || file.status === 'generating';
                    return (
                      <React.Fragment key={file.id}>
                        <div className="knowledge-item">
                          <h3 className="knowledge-link" title={file.document_name}>
                            <div className="knowledge-status-link" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabledRow}
                                onChange={() => toggleGoogleDriveSelected(file.id)}
                                title={checked ? t('unselect') : t('select')}
                                style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                              />
                              {file.status === 'scraping' || file.status === 'generating' ? (
                                <div className="knowledge-loading-spinner" />
                              ) : file.status === 'failed' ? (
                                <FailedStatusIcon reason={file.failure_reason} />
                              ) : (
                                <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />
                              )}
                              <svg width="18" height="16" viewBox="0 0 87.3 78" style={{ marginRight: '6px', flexShrink: 0 }}>
                                <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/>
                                <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"/>
                                <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"/>
                                <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/>
                                <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/>
                                <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/>
                              </svg>
                              {file.document_name}
                            </div>
                          </h3>
                          <FailureReason status={file.status} reason={file.failure_reason} />

                          <div className="knowledge-date-action-container">
                            <div className="knowledge-created-at">
                              <div className="knowledge-created-at-icon">
                                <FontAwesomeIcon icon={faCalendar} />
                              </div>
                              <p>{formatDate(file.created_at)}</p>
                            </div>

                            <div className="knowledge-actions">
                              <button
                                className="knowledge-action-button"
                                onClick={() => handleOpenKnowledgeDocumentModal(file.id)}
                                disabled={file.status === 'scraping' || file.status === 'generating'}
                                title={t('view_data')}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {index < googleDriveFiles.length - 1 && <div className="knowledge-item-separator" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {googleDriveFiles.length === 0 && (
                <div style={{ padding: '20px', paddingTop: '0', color: '#6b7280' }}>
                  {t('googledrive_empty')}
                </div>
              )}

              {googleDriveFiles.length > 3 && (
                <button className="expand-toggle" onClick={toggleGoogleDriveExpand}>
                  <FontAwesomeIcon icon={googleDriveIsExpanded ? faChevronUp : faChevronDown} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'onedrive' && (
        <>
          <button
            className="knowledge-add-button"
            title={t('import_from_onedrive') || 'Import from OneDrive'}
            onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : handleOpenOnedrivePicker()}
          >
            <div className="knowledge-add-button-text">
              <FontAwesomeIcon icon={faPlus} /> {t('import_from_onedrive') || 'Import from OneDrive'}
            </div>
          </button>
          <div className='rescraping-activate-settings-container'>
            {rescrapingToggleLoading ? (
              <div className='rescraping-toggle-loading'>
                <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
                <span>{t("auto_sync_files")}</span>
              </div>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!connectorSyncTasks[activeConnectorProvider]?.active}
                    onChange={(e) => handleToggleConnectorSync(activeConnectorProvider, e.target.checked)}
                    disabled={false}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5616ea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#5616ea',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#ccc',
                      },
                    }}
                  />
                }
                className='rescraping-toggle'
                label={t("auto_sync_files")}
              />
            )}            {connectorSyncTasks[activeConnectorProvider]?.active && (
              <button
                className='rescraping-settings-button'
                onClick={() => setConnectorSyncModalProvider(activeConnectorProvider)}
              >
                <FontAwesomeIcon icon={faSliders} />
              </button>
            )}
          </div>
          <div className="knowledge-add-button-parent">
            <div className={`knowledge-container ${onedriveIsExpanded ? '' : 'collapsed'}`}>
              <div className="knowledge-header">
                <div className="header-title-with-search">
                  <h3 className="header-title">OneDrive</h3>
                  <div className="knowledge-search-container">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                    <input
                      type="text"
                      className="knowledge-search-input"
                      placeholder={t('search') || 'Search...'}
                      value={searchOnedrive}
                      onChange={(e) => setSearchOnedrive(e.target.value)}
                    />
                    {searchOnedrive && (
                      <button
                        type="button"
                        className="knowledge-search-clear"
                        onClick={() => setSearchOnedrive('')}
                        aria-label={t('clear_search')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>
                    {selectedOnedriveIds.length} {t('selected')}
                  </span>

                  <button
                    className="knowledge-action-button"
                    onClick={handleBulkRescrapeOnedrive}
                    disabled={!isAnyOnedriveSelected}
                    title={t('rescrape')}
                    style={{ opacity: isAnyOnedriveSelected ? 1 : 0.5, cursor: isAnyOnedriveSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </button>
                  <button
                    className="knowledge-action-button"
                    onClick={() => confirmBulkDelete('onedrive')}
                    disabled={!isAnyOnedriveSelected}
                    title={t('remove')}
                    style={{ opacity: isAnyOnedriveSelected ? 1 : 0.5, cursor: isAnyOnedriveSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              {onedriveFiles.length > 0 && (
                <div className="knowledge-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '10px', fontSize: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}>
                      <input
                        ref={onedriveSelectAllRef}
                        type="checkbox"
                        checked={allOnedriveSelected}
                        onChange={toggleSelectAllOnedrive}
                        title={allOnedriveSelected ? t('unselect') : t('select')}
                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                      />
                      <span>{t('select_all')}</span>
                    </label>
                    {onedriveScrapingCount > 0 && (
                      <div className="scraping-links-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin className="scraping-indicator-icon" />
                        <span className="scraping-indicator-text">
                          {t('files_currently_importing', { count: onedriveScrapingCount })}
                        </span>
                      </div>
                    )}
                  </div>
                  {(onedriveIsExpanded
                    ? [...onedriveFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchOnedrive.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length)
                    : [...onedriveFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchOnedrive.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length).slice(0, 3)
                  ).map((file, index) => {
                    const checked = selectedOnedriveIds.includes(file.id);
                    const disabledRow = file.status === 'scraping' || file.status === 'generating';
                    return (
                      <React.Fragment key={file.id}>
                        <div className="knowledge-item">
                          <h3 className="knowledge-link" title={file.document_name}>
                            <div className="knowledge-status-link" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabledRow}
                                onChange={() => toggleOnedriveSelected(file.id)}
                                title={checked ? t('unselect') : t('select')}
                                style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                              />
                              {file.status === 'scraping' || file.status === 'generating' ? (
                                <div className="knowledge-loading-spinner" />
                              ) : file.status === 'failed' ? (
                                <FailedStatusIcon reason={file.failure_reason} />
                              ) : (
                                <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />
                              )}
                              <img src="/onedrive.png" alt="OneDrive" style={{ height: '16px', width: 'auto', marginRight: '6px', flexShrink: 0 }} />
                              {file.document_name}
                            </div>
                          </h3>
                          <FailureReason status={file.status} reason={file.failure_reason} />

                          <div className="knowledge-date-action-container">
                            <div className="knowledge-created-at">
                              <div className="knowledge-created-at-icon">
                                <FontAwesomeIcon icon={faCalendar} />
                              </div>
                              <p>{formatDate(file.created_at)}</p>
                            </div>

                            <div className="knowledge-actions">
                              <button
                                className="knowledge-action-button"
                                onClick={() => handleOpenKnowledgeDocumentModal(file.id)}
                                disabled={file.status === 'scraping' || file.status === 'generating'}
                                title={t('view_data')}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {index < onedriveFiles.length - 1 && <div className="knowledge-item-separator" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {onedriveFiles.length === 0 && (
                <div style={{ padding: '20px', paddingTop: '0', color: '#6b7280' }}>
                  {t('onedrive_empty')}
                </div>
              )}

              {onedriveFiles.length > 3 && (
                <button className="expand-toggle" onClick={toggleOnedriveExpand}>
                  <FontAwesomeIcon icon={onedriveIsExpanded ? faChevronUp : faChevronDown} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'notion' && (
        <>
          <button
            className="knowledge-add-button"
            title={t('import_from_notion') || 'Import from Notion'}
            onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : handleOpenNotionPicker()}
          >
            <div className="knowledge-add-button-text">
              <FontAwesomeIcon icon={faPlus} /> {t('import_from_notion') || 'Import from Notion'}
            </div>
          </button>
          <div className='rescraping-activate-settings-container'>
            {rescrapingToggleLoading ? (
              <div className='rescraping-toggle-loading'>
                <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#5616ea', marginRight: '8px' }} />
                <span>{t("auto_sync_files")}</span>
              </div>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!connectorSyncTasks[activeConnectorProvider]?.active}
                    onChange={(e) => handleToggleConnectorSync(activeConnectorProvider, e.target.checked)}
                    disabled={false}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#5616ea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#5616ea',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#ccc',
                      },
                    }}
                  />
                }
                className='rescraping-toggle'
                label={t("auto_sync_files")}
              />
            )}            {connectorSyncTasks[activeConnectorProvider]?.active && (
              <button
                className='rescraping-settings-button'
                onClick={() => setConnectorSyncModalProvider(activeConnectorProvider)}
              >
                <FontAwesomeIcon icon={faSliders} />
              </button>
            )}
          </div>
          <div className="knowledge-add-button-parent">
            <div className={`knowledge-container ${notionIsExpanded ? '' : 'collapsed'}`}>
              <div className="knowledge-header">
                <div className="header-title-with-search">
                  <h3 className="header-title">Notion</h3>
                  <div className="knowledge-search-container">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                    <input
                      type="text"
                      className="knowledge-search-input"
                      placeholder={t('search') || 'Search...'}
                      value={searchNotion}
                      onChange={(e) => setSearchNotion(e.target.value)}
                    />
                    {searchNotion && (
                      <button
                        type="button"
                        className="knowledge-search-clear"
                        onClick={() => setSearchNotion('')}
                        aria-label={t('clear_search')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280' }}>
                    {selectedNotionIds.length} {t('selected')}
                  </span>

                  <button
                    className="knowledge-action-button"
                    onClick={handleBulkRescrapeNotion}
                    disabled={!isAnyNotionSelected}
                    title={t('rescrape')}
                    style={{ opacity: isAnyNotionSelected ? 1 : 0.5, cursor: isAnyNotionSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </button>
                  <button
                    className="knowledge-action-button"
                    onClick={() => confirmBulkDelete('notion')}
                    disabled={!isAnyNotionSelected}
                    title={t('remove')}
                    style={{ opacity: isAnyNotionSelected ? 1 : 0.5, cursor: isAnyNotionSelected ? 'pointer' : 'not-allowed' }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              {notionFiles.length > 0 && (
                <div className="knowledge-content">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '10px', fontSize: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}>
                      <input
                        ref={notionSelectAllRef}
                        type="checkbox"
                        checked={allNotionSelected}
                        onChange={toggleSelectAllNotion}
                        title={allNotionSelected ? t('unselect') : t('select')}
                        style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                      />
                      <span>{t('select_all')}</span>
                    </label>
                    {notionScrapingCount > 0 && (
                      <div className="scraping-links-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin className="scraping-indicator-icon" />
                        <span className="scraping-indicator-text">
                          {t('pages_currently_importing', { count: notionScrapingCount })}
                        </span>
                      </div>
                    )}
                  </div>
                  {(notionIsExpanded
                    ? [...notionFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchNotion.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length)
                    : [...notionFiles].filter(f => (f.document_name || '').toLowerCase().includes(searchNotion.toLowerCase())).sort((a, b) => (a.document_name || '').length - (b.document_name || '').length).slice(0, 3)
                  ).map((file, index) => {
                    const checked = selectedNotionIds.includes(file.id);
                    const disabledRow = file.status === 'scraping' || file.status === 'generating';
                    return (
                      <React.Fragment key={file.id}>
                        <div className="knowledge-item">
                          <h3 className="knowledge-link" title={file.document_name}>
                            <div className="knowledge-status-link" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabledRow}
                                onChange={() => toggleNotionSelected(file.id)}
                                title={checked ? t('unselect') : t('select')}
                                style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                              />
                              {file.status === 'scraping' || file.status === 'generating' ? (
                                <div className="knowledge-loading-spinner" />
                              ) : file.status === 'failed' ? (
                                <FailedStatusIcon reason={file.failure_reason} />
                              ) : (
                                <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />
                              )}
                              <NotionLogo size={16} style={{ marginRight: '6px', flexShrink: 0 }} />
                              {file.document_name}
                            </div>
                          </h3>
                          <FailureReason status={file.status} reason={file.failure_reason} />

                          <div className="knowledge-date-action-container">
                            <div className="knowledge-created-at">
                              <div className="knowledge-created-at-icon">
                                <FontAwesomeIcon icon={faCalendar} />
                              </div>
                              <p>{formatDate(file.created_at)}</p>
                            </div>

                            <div className="knowledge-actions">
                              <button
                                className="knowledge-action-button"
                                onClick={() => handleOpenKnowledgeDocumentModal(file.id)}
                                disabled={file.status === 'scraping' || file.status === 'generating'}
                                title={t('view_data')}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {index < notionFiles.length - 1 && <div className="knowledge-item-separator" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {notionFiles.length === 0 && (
                <div style={{ padding: '20px', paddingTop: '0', color: '#6b7280' }}>
                  {t('notion_empty')}
                </div>
              )}

              {notionFiles.length > 3 && (
                <button className="expand-toggle" onClick={toggleNotionExpand}>
                  <FontAwesomeIcon icon={notionIsExpanded ? faChevronUp : faChevronDown} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'faq' && (
        <>
          <button
            className="knowledge-add-button"
            title={t('add_qa')}
            onClick={() => isAtKnowledgeLimit ? setKnowledgeUpgradeModalOpen(true) : setAddFaqOpen(true)}
          >
            <div className="knowledge-add-button-text"><FontAwesomeIcon icon={faPlus} /> {t('add_qa')}</div>
          </button>

          <div className="knowledge-container">
            <div className="knowledge-header">
              <div className="header-title-with-search">
                <h3 className="header-title">{t('qa_label')}</h3>
                <div className="knowledge-search-container">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                  <input
                    type="text"
                    className="knowledge-search-input"
                    placeholder={t('search') || 'Search...'}
                    value={searchFaq}
                    onChange={(e) => setSearchFaq(e.target.value)}
                  />
                  {searchFaq && (
                    <button
                      type="button"
                      className="knowledge-search-clear"
                      onClick={() => setSearchFaq('')}
                      aria-label={t('clear_search')}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              </div>
              <p style={{fontSize: '15px'}}>{t('count')}: {faqs.length}</p>
            </div>

            {faqs.length > 0 ? (
              <div className="knowledge-content">
                {faqs.filter(item => extractFaqQuestion(item.description).toLowerCase().includes(searchFaq.toLowerCase())).map((item, index) => (
                  <React.Fragment key={item.id}>
                    <div className="knowledge-item">
                      <h3 className="knowledge-link" title={extractFaqQuestion(item.relevant_data)}>
                        <div className="knowledge-status-link">
                          {item.status === 'scraping' || item.status === 'processing' ? (
                            <div className="knowledge-loading-spinner" />
                          ) : item.status === 'failed' ? (
                            <FailedStatusIcon reason={item.failure_reason} />
                          ) : (
                            <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />
                          )}
                          {extractFaqQuestion(item.description)}
                        </div>
                      </h3>
                      <FailureReason status={item.status} reason={item.failure_reason} />

                      <div className="knowledge-date-action-container">
                        <div className="knowledge-created-at">
                          <div className="knowledge-created-at-icon">
                            <FontAwesomeIcon icon={faCalendar} />
                          </div>
                          <p>{formatDate(item.created_at)}</p>
                        </div>

                        <div className="knowledge-actions">
                          <button
                            className="knowledge-action-button"
                            onClick={() => handleOpenEditFaqModal(item)}
                            disabled={item.status === 'scraping' || item.status === 'processing'}
                            title={t('edit')}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                          <button
                            className="knowledge-action-button"
                            onClick={() => {
                              setSelectedKnowledgeId(item.id);
                              setFaqModalOpen(true);
                            }}
                            disabled={item.status === 'scraping' || item.status === 'processing'}
                            title={t('view_data')}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            className="knowledge-action-button"
                            onClick={() => handleOpenDeleteModal(item)}
                            disabled={item.status === 'scraping' || item.status === 'processing'}
                            title={t('remove')}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {index < faqs.length - 1 && <div className="knowledge-item-separator" />}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div style={{ padding: '20px', paddingTop: '0', color: '#6b7280' }}>
                {t('faq_empty')}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'business_context' && (
        <div className="business-context-parent-container">
          <div className="business-context-info-box">
            <FontAwesomeIcon className="business-context-info-icon" icon={faCircleInfo} />
            <div className="business-context-info-text">
              <p>
                <Trans i18nKey="business_context_info_html" components={{ strong: <strong /> }} />
              </p>
              <p style={{ marginTop: '12px' }}>
                <Trans i18nKey="business_context_include_html" values={{ max: BUSINESS_CONTEXT_MAX }} components={{ strong: <strong /> }} />
              </p>
            </div>
          </div>

          <div className="business-context-editor">
            <TextField
              fullWidth
              label={t('business_context')}
              variant="outlined"
              value={businessContext}
              disabled={!contextEditor.loaded || businessContextSaving}
              onChange={(e) => { contextGen.clear(); setBusinessContext(e.target.value); }}
              margin="normal"
              multiline
              minRows={6}
              inputProps={{ maxLength: BUSINESS_CONTEXT_MAX }}
              helperText={`${businessContext.length}/${BUSINESS_CONTEXT_MAX}`}
              FormHelperTextProps={{
                sx: {
                  textAlign: 'right',
                  mr: 0,
                  color: businessContext.length === BUSINESS_CONTEXT_MAX ? 'error.main' : 'text.secondary',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#515151' },
                '& .MuiInputLabel-root': { color: 'gray', '&.Mui-focused': { color: '#5616ea' } },
              }}
            />

            {contextGenNote && (
              <div role="status" className={`business-context-gen-note ${contextGenNote.tone === 'ok' ? 'is-ok' : 'is-warn'}`}>
                <FontAwesomeIcon icon={faWandMagicSparkles} className="business-context-gen-note-icon" />
                <span>
                  {contextGenNote.text}
                  {contextGenNote.knowledge && (
                    <> <button type="button" className="business-context-gen-link" onClick={() => requestUnsavedAction(() => navigate('/knowledge/website-links'))}>
                      {t('agent_studio_knowledge_manage')}
                    </button></>
                  )}
                </span>
              </div>
            )}

            {contextEditor.error && (
              <div role="alert" className="business-context-error">
                <p>{t(contextEditor.error)}</p>
                {(contextEditor.conflict || !contextEditor.loaded) && (
                  <button type="button" className="discard-widget-btn" onClick={contextEditor.reload} disabled={businessContextSaving}>
                    {t(contextEditor.conflict ? 'business_context_reload_discard' : 'business_context_retry')}
                  </button>
                )}
              </div>
            )}

            {businessContextChanged && !contextEditor.conflict && (
              <div className="unsaved-changes-message">
                <div className="warning-icon">⚠️</div>
                <div className="warning-text">{t('unsaved_changes_info')}</div>
              </div>
            )}

            <div className="save-discard-buttons">
              <button
                className="save-widget-btn"
                onClick={handleSaveBusinessContext}
                disabled={businessContextSaving || !contextEditor.loaded || contextEditor.conflict}
              >
                {businessContextSaving && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />}
                {businessContextSaving ? t('saving') : t('save')}
              </button>
              {businessContextChanged && !contextEditor.conflict && (
                <button onClick={handleDiscardBusinessContext} className="discard-widget-btn">
                  {t('discard_changes')}
                </button>
              )}
              <span className="business-context-generate"
                title={t(businessContextChanged && !contextGen.running ? 'business_context_generate_unsaved' : 'business_context_generate_hint')}>
                <button type="button" className="business-context-generate-btn" onClick={handleGenerateBusinessContext}
                  disabled={contextGen.running || businessContextChanged || !contextEditor.loaded || businessContextSaving}>
                  <FontAwesomeIcon icon={contextGen.running ? faSpinner : faWandMagicSparkles} spin={contextGen.running} />
                  {t(contextGen.running ? 'business_context_generating' : 'business_context_generate')}
                </button>
              </span>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onConfirm={handleDeleteKnowledge}
        itemName={knowledgeToDelete?.parameter}
        requireTextConfirmation={false}
      />

      <ConfirmationModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        onConfirm={
          bulkDeleteContext.type === 'links'
            ? handleBulkDeleteWebsites
            : bulkDeleteContext.type === 'googledrive'
              ? handleBulkDeleteGoogleDrive
              : bulkDeleteContext.type === 'onedrive'
                ? handleBulkDeleteOnedrive
                : bulkDeleteContext.type === 'notion'
                  ? handleBulkDeleteNotion
                  : handleBulkDeleteDocuments
        }
        itemName={bulkDeleteContext.label || ''}
        requireTextConfirmation={false}
      />

      <KnowledgeModal
        open={knowledgeModalOpen}
        onClose={() => setKnowledgeModalOpen(false)}
        knowledgeId={selectedKnowledgeId}
      />

      <FaqModal
        open={faqModalOpen}
        onClose={() => setFaqModalOpen(false)}
        knowledgeId={selectedKnowledgeId}
      />

      <KnowledgeDocumentModal
        open={knowledgeDocumentModalOpen}
        onClose={() => setKnowledgeDocumentModalOpen(false)}
        knowledgeId={selectedKnowledgeDocumentId}
      />

      <AddWebsiteLinkModal
        open={addWebsiteLinkModalOpen}
        onClose={() => setAddWebsiteLinkModalOpen(false)}
        onAddLinks={handleAddWebsiteLink}
        onRescrapeLinks={handleRescrapeLinksFromModal}
        onRemoveLinks={handleRemoveLinksFromModal}
        botId={bot.id}
        botUrl={bot.url}
        existingLinks={websiteLinks}
        remainingSlots={remainingSlots}
        onUpgrade={() => handleUpgradeClick(bot)}
      />

      <CheckForUpdatesModal
        open={checkUpdatesModalOpen}
        onClose={() => setCheckUpdatesModalOpen(false)}
        onApply={handleUpdateCheckApply}
        botId={bot.id}
        existingLinks={websiteLinks}
        remainingSlots={remainingSlots}
        onUpgrade={() => handleUpgradeClick(bot)}
      />

      <UploadFileModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        onSubmit={handleAddDocument}
        remainingSlots={remainingSlots}
      />

      <RescrapingSettingsModal
        open={rescrapeModalOpen}
        onClose={() => setRescrapeModalOpen(false)}
        onSave={handleSaveRescrape}
        bot={bot}
        onOpenSubscriptionModal={handleUpgradeClick}
      />

      <ConnectorSyncSettingsModal
        open={!!connectorSyncModalProvider}
        onClose={() => setConnectorSyncModalProvider(null)}
        onSave={handleSaveConnectorSync}
        bot={bot}
        provider={connectorSyncModalProvider}
        config={connectorSyncTasks[connectorSyncModalProvider]}
        onOpenSubscriptionModal={handleUpgradeClick}
      />

      <AddFaqModal
        open={addFaqOpen}
        onClose={() => { if (!faqSaving) { setAddFaqOpen(false); setFaqSaveError(null); } }}
        onAddFaq={handleAddFaqViaModal}
        submitting={faqSaving}
        error={faqSaveError}
      />

      <EditFaqModal
        open={editFaqOpen}
        onClose={() => { setEditFaqOpen(false); setFaqToEdit(null); }}
        onEditFaq={handleEditFaqViaModal}
        faqItem={faqToEdit}
        submitting={loading}
      />

      <GoogleDrivePickerModal
        isOpen={googleDrivePickerOpen}
        onClose={() => setGoogleDrivePickerOpen(false)}
        onFilesSelected={handleGoogleDriveFilesSelected}
        botId={bot?.id}
        remainingSlots={remainingSlots}
        onUpgrade={() => handleUpgradeClick(bot)}
      />

      <OneDrivePickerModal
        isOpen={onedrivePickerOpen}
        onClose={() => setOnedrivePickerOpen(false)}
        onFilesSelected={handleOnedriveFilesSelected}
        botId={bot?.id}
        remainingSlots={remainingSlots}
        onUpgrade={() => handleUpgradeClick(bot)}
      />

      <NotionPickerModal
        isOpen={notionPickerOpen}
        onClose={() => setNotionPickerOpen(false)}
        onFilesSelected={handleNotionFilesSelected}
        botId={bot?.id}
        remainingSlots={remainingSlots}
        onUpgrade={() => handleUpgradeClick(bot)}
      />

      <KnowledgeUpgradeModal
        open={knowledgeUpgradeModalOpen}
        onClose={() => setKnowledgeUpgradeModalOpen(false)}
        bot={bot}
        onUpgrade={() => handleUpgradeClick(bot)}
        currentKnowledgeCount={knowledgeCount}
        loading={isUpgradeLoading(bot?.id)}
      />

      {UpgradeModal}
    </div>
  );
}

export default KnowledgeOverview;
